import { memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import * as global from "../data/global";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../index.js";
import {
    BIN_TYPES,
    DEVICE_TYPES,
    MODULE_TYPES,
    SECTION_TYPES,
    applyModuleTypeChange,
    getModuleModelOptions,
    createDefaultBin,
    createDefaultCable,
    createDefaultDevice,
    createDefaultFan,
    createDefaultModule,
    createDefaultSection,
    createDefaultSensor,
    renumberBins,
    renumberDevices,
    refreshSetupSection,
    resizeList,
    syncCablesFromBins,
    syncFansFromBins,
    syncModulesFromDevices,
    syncSectionsFromDevices,
    propagateDeviceIpAddress,
    syncSensorsFromCables,
    prepareSetupForSave,
    prepareMainForSave,
    updateBinCableCounts,
    updateBinFanCounts,
    cableCountFromLayout,
    figureBinCableLayout,
    applyBinCableLayouts,
    syncSectionPositionRefsForSection,
    syncAllSectionPositionRefs,
    applySectionCablesPerSlotToRefs,
    sortSectionPositionRefs,
    assignSectionsToCables,
    effectiveSectionSlots,
    totalSectionAssignCapacity,
    nextTempSetupId,
} from "./siteSetupDefaults.js";
import {
    createEmptyTaskSchedule,
    hydrateTaskSetupFromPayload,
} from "./siteSetupTasks.js";
import SetupTasksPanel from "./siteSetupTasksPanel.js";
import SetupConfigPanel from "./siteSetupConfigPanel.js";
import { SiteWindowHeaderLeading, SiteWindowToolNav, SITE_WINDOW_IDS, InfoHint, ChromeHeaderBar, withScrollHintMenuProps, withMenuScrollHintTheme, SiteSelectPhase, NEW_SITE_OPTION, ConfirmDialog, confirmDialogButtonSx } from "./windowChrome/index.js";

const EMPTY_SETUP = {
    main: null,
    devices: [],
    modules: [],
    sections: [],
    sectionPositionRefs: [],
    bins: [],
    fans: [],
    cables: [],
    sensors: [],
    tasks: [],
    taskSchedule: createEmptyTaskSchedule(),
    taskSelectedBins: [],
};

/** BTS Main.SiteName = Customer Name + space + Site Name. */
function formatMainSiteName(customerName, siteName) {
    return `${String(customerName ?? "").trim()} ${String(siteName ?? "").trim()}`.trim() || "New Site";
}

/** Seed a blank editable setup for a freshly provisioned site. */
function createNewSiteSetup(mainSiteName) {
    return {
        ...EMPTY_SETUP,
        main: { siteName: mainSiteName || "New Site", bins: 0, devices: 0, emailReportTo: "", isAutoScanOn: false },
        devices: [],
        modules: [],
        sections: [],
        sectionPositionRefs: [],
        bins: [],
        fans: [],
        cables: [],
        sensors: [],
        tasks: [],
        taskSchedule: createEmptyTaskSchedule(),
        taskSelectedBins: [],
    };
}

function getThemeColor(lightColor, darkColor) {
    return global.gw.theme === "light" ? lightColor : darkColor;
}

function createSiteSetupTheme() {
    const isLight = global.gw.theme === "light";
    const textPrimary = isLight ? "#333333" : "#f4cc8e";
    const textSecondary = isLight ? "#666666" : "#c4a574";
    const paper = isLight ? "#ffffff" : "#2d2d2d";
    // Match window chrome / site-cards list borders (mustard-tint in dark).
    const border = isLight ? "#cccccc" : "#f4cc8e4d";
    const pageBg = isLight ? "#e8e8e8" : "#1a1a1a";
    const sectionBg = isLight ? "#f8f8f8" : "#313131";

    return {
        textPrimary,
        textSecondary,
        paper,
        border,
        pageBg,
        sectionBg,
        theme: MuiMaterials.createTheme({
            palette: {
                mode: isLight ? "light" : "dark",
                text: { primary: textPrimary, secondary: textSecondary },
                background: { default: isLight ? "#f1f1f1" : "#121212", paper },
            },
            components: withMenuScrollHintTheme({
                MuiTypography: {
                    styleOverrides: { root: { color: textPrimary } },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: isLight ? "#000000" : "#f4cc8e",
                            "&.Mui-focused": { color: isLight ? "#000000" : "#f4cc8e" },
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: textPrimary,
                            fontSize: 13,
                            backgroundColor: paper,
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
                            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: isLight ? "#666666" : "#f4cc8e" },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: isLight ? "#666666" : "#f4cc8e" },
                        },
                        input: { padding: "6px 8px" },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            textTransform: "none",
                            color: textPrimary,
                            background: isLight ? "#e0e0e0" : "#2d2d2d",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            // Override global `button { font-size: calc(...) }` so labels sit on the vertical center.
                            fontSize: "0.875rem",
                            lineHeight: 1.25,
                            paddingTop: 6,
                            paddingBottom: 6,
                            minHeight: 36,
                            "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                                display: "inline-flex",
                                alignItems: "center",
                                marginTop: 0,
                                marginBottom: 0,
                            },
                        },
                        sizeSmall: {
                            fontSize: "0.8125rem",
                            lineHeight: 1,
                            padding: "6px 12px",
                            minHeight: 32,
                        },
                    },
                },
                MuiIconButton: {
                    styleOverrides: { root: { color: textPrimary } },
                },
                MuiCheckbox: {
                    styleOverrides: { root: { color: textPrimary } },
                },
                MuiMenuItem: {
                    styleOverrides: { root: { color: textPrimary } },
                },
                MuiSelect: {
                    styleOverrides: {
                        icon: { color: textPrimary },
                    },
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: { color: textPrimary, borderColor: border, fontSize: 13 },
                        head: { fontWeight: 600, backgroundColor: isLight ? "#f5f5f5" : "#252525", color: textPrimary },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: "none",
                            backgroundColor: paper,
                            color: textPrimary,
                        },
                    },
                },
                MuiSvgIcon: {
                    styleOverrides: { root: { color: textPrimary } },
                },
            }),
        }),
    };
}

function LoadingOverlay({ forceUpdate }) {
    return (
        <MuiMaterials.Box
            key={`site-setup-spinner-${global.gw.theme}-${forceUpdate}`}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "fixed",
                inset: 0,
                background: getThemeColor("rgba(255, 255, 255, 0.9)", "rgba(0, 0, 0, 0.8)"),
                zIndex: 10001,
            }}
        >
            <MuiMaterials.RotatingLines
                strokeColor={getThemeColor("#333333", "#f4cc8e")}
                strokeWidth="3"
                animationDuration="0.72"
                width="48"
                visible
            />
        </MuiMaterials.Box>
    );
}

function SetupTextField({ value, onChange, type = "text", sx }) {
    return (
        <MuiMaterials.TextField
            size="small"
            variant="outlined"
            fullWidth
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
            sx={sx}
        />
    );
}

function mainSetupEmailValue(main, camelKey, pascalKey) {
    if (!main) return "";
    return String(main[camelKey] ?? main[pascalKey] ?? "");
}

function MainSetupEmailArea({ label, tooltip, value, onChange }) {
    return (
        <MuiMaterials.Box sx={{ mt: 1.5, px: 0.5 }}>
            <MuiMaterials.Stack direction="row" spacing={0.25} alignItems="center" sx={{ mb: 0.5 }}>
                <MuiMaterials.Typography
                    component="label"
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        fontSize: 12,
                    }}
                >
                    {label}
                </MuiMaterials.Typography>
                <InfoHint title={tooltip} ariaLabel={`${label} help`} />
            </MuiMaterials.Stack>
            <MuiMaterials.TextField
                size="small"
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Comma or semicolon-separated addresses"
            />
        </MuiMaterials.Box>
    );
}

function SetupCheckbox({ checked, onChange }) {
    return (
        <MuiMaterials.Checkbox
            size="small"
            checked={!!checked}
            onChange={(e) => onChange(e.target.checked)}
            sx={{ p: 0.5 }}
        />
    );
}

const ROW_RENDER_CHUNK = 25;
const SECTION_EXPAND_SCROLL_EXTRA = 16;

const HIDE_SCROLLBAR_SX = {
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
};

function setupCellId(sectionId, rowIndex, columnKey) {
    return `${sectionId}-${rowIndex}-${columnKey}`;
}

function focusSetupCell(sectionId, rowIndex, columnKey) {
    const id = setupCellId(sectionId, rowIndex, columnKey);
    const el = document.querySelector(`[data-setup-cell="${id}"]`);
    if (!el) return;
    el.focus();
    if (typeof el.select === "function") {
        el.select();
    }
}

/** BTS Setup txt_KeyDown: trailing digits +1 on Down, -1 on Up when Tag=Increment. */
function copyValueForArrowNav(value, { increment, direction }) {
    if (!increment) return value;
    const text = value == null ? "" : String(value);
    const match = text.match(/(\d+)$/);
    if (!match) return value;
    const delta = direction === "down" ? 1 : -1;
    const num = parseInt(match[1], 10) + delta;
    return text.slice(0, -match[1].length) + String(num);
}

function parseArrowNavValue(nextValue, valueType) {
    if (valueType === "number") {
        if (nextValue === "" || nextValue == null) return "";
        const n = Number(nextValue);
        return Number.isNaN(n) ? nextValue : n;
    }
    return nextValue;
}

function handleSetupArrowKeyDown(e, {
    sectionId,
    rowIndex,
    rowCount,
    columnKey,
    increment,
    valueType,
    value,
    onNavigate,
}) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    e.stopPropagation();
    const direction = e.key === "ArrowDown" ? "down" : "up";
    const targetRow = direction === "down" ? rowIndex + 1 : rowIndex - 1;
    if (targetRow < 0 || targetRow >= rowCount) return;
    const nextValue = copyValueForArrowNav(value, { increment, direction });
    onNavigate(targetRow, columnKey, parseArrowNavValue(nextValue, valueType));
    requestAnimationFrame(() => {
        requestAnimationFrame(() => focusSetupCell(sectionId, targetRow, columnKey));
    });
}

/** Column input width in `ch` units � longest label/value/option in the column. */
function computeColumnSizeCh(col, rows) {
    // Explicit pixel-ish `width` on the column wins (Bin Setup etc. use these to keep the row fitting).
    if (col.width > 0) {
        return Math.max(col.type === "number" ? 3 : 2, Math.round(col.width / 9));
    }
    const texts = [col.label ?? ""];
    if (col.measureValues?.length) {
        for (const v of col.measureValues) {
            texts.push(String(v ?? ""));
        }
    }
    for (const row of rows) {
        texts.push(String(row[col.key] ?? ""));
    }
    const maxLen = Math.max(...texts.map((t) => t.length), 1);
    const min = col.type === "number" ? 3 : 2;
    // Selects need room for the caret; numbers/text stay tight to the content.
    const extra = col.measureValues ? 3 : col.type === "number" ? 0 : 1;
    return Math.max(min, maxLen) + extra;
}

function setupFieldWidthSx(sizeCh) {
    return {
        width: "100%",
        boxSizing: "border-box",
        ...(sizeCh ? { minWidth: `${sizeCh}ch` } : {}),
    };
}

/** Show fill-down to the right of fields for any column that supports arrow-key fill unless opted out. */
function columnShowsFillDownIcon(col) {
    return col.fillDown !== false;
}

function FillDownKeyHint({ onClick }) {
    const border = getThemeColor("#cccccc", "#f4cc8e66");
    const paper = getThemeColor("#f0f0f0", "#3a3a3a");
    const textSecondary = getThemeColor("#666666", "#c4a574");
    return (
        <MuiMaterials.Box
            component="span"
            className="fill-down-hint"
            role="button"
            tabIndex={-1}
            aria-label="Fill all rows below"
            title="Fill all rows below"
            onClick={(e) => {
                e.stopPropagation();
                onClick?.();
            }}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 16,
                height: 14,
                fontSize: 10,
                lineHeight: 1,
                color: textSecondary,
                background: paper,
                border: `1px solid ${border}`,
                borderRadius: "3px",
                boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.12)",
                cursor: "pointer",
                userSelect: "none",
                pointerEvents: "auto",
            }}
        >
            ↓
        </MuiMaterials.Box>
    );
}

function SetupColumnHeader({ label, tooltip, minCh, widthPct, headerSx }) {
    const headerTip = tooltip || label;
    const headerText = (
        <MuiMaterials.Box
            component="span"
            sx={{
                cursor: headerTip ? "help" : undefined,
            }}
        >
            {label}
        </MuiMaterials.Box>
    );
    return (
        <MuiMaterials.TableCell
            sx={{
                fontWeight: 600,
                fontSize: 12,
                whiteSpace: "nowrap",
                px: 0.5,
                py: 0.75,
                overflow: "hidden",
                textOverflow: "ellipsis",
                ...(widthPct != null
                    ? { width: `${widthPct}%` }
                    : minCh
                        ? { width: `${minCh}ch` }
                        : {}),
                ...headerSx,
            }}
        >
            {headerTip ? (
                <MuiMaterials.Tooltip title={headerTip} arrow placement="top">
                    {headerText}
                </MuiMaterials.Tooltip>
            ) : (
                headerText
            )}
        </MuiMaterials.TableCell>
    );
}

function setupDataCellSx(minCh, widthPct) {
    return {
        verticalAlign: "middle",
        px: 0.5,
        py: 0.5,
        overflow: "hidden",
        ...(widthPct != null
            ? { width: `${widthPct}%` }
            : minCh
                ? { width: `${minCh}ch` }
                : {}),
    };
}

function SetupCellWrap({ children, showFillDown, onFillDown, onKeyDownCapture }) {
    if (!showFillDown && !onKeyDownCapture) return children;
    const wrapSx = {
        position: "relative",
        display: "block",
        width: "100%",
        // Keep fill-down out of layout width — it overlays the field edge on hover.
        "& .fill-down-hint": {
            position: "absolute",
            right: 2,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0,
            transition: "opacity 0.12s ease",
            zIndex: 1,
        },
        "&:hover .fill-down-hint, &:focus-within .fill-down-hint": {
            opacity: 1,
        },
    };
    return (
        <MuiMaterials.Box sx={wrapSx} onKeyDownCapture={onKeyDownCapture}>
            {children}
            {showFillDown ? <FillDownKeyHint onClick={onFillDown} /> : null}
        </MuiMaterials.Box>
    );
}

function SetupSelect({
    value,
    options,
    onChange,
    sectionId,
    rowIndex,
    rowCount,
    columnKey,
    increment = false,
    valueType,
    onNavigate,
    onFillDown,
    showFillDownIcon = false,
    sizeCh,
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    const cellId = sectionId != null && columnKey != null
        ? setupCellId(sectionId, rowIndex, columnKey)
        : undefined;
    const arrowEnabled = sectionId != null && onNavigate != null && rowCount != null;
    const showFillDown = showFillDownIcon && arrowEnabled && rowIndex < rowCount - 1;
    const arrowProps = {
        sectionId,
        rowIndex,
        rowCount,
        columnKey,
        increment,
        valueType,
        value,
        onNavigate,
    };
    const captureArrowKeys = arrowEnabled && !menuOpen
        ? (e) => {
            if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
            handleSetupArrowKeyDown(e, arrowProps);
        }
        : undefined;

    return (
        <SetupCellWrap
            showFillDown={showFillDown}
            onFillDown={onFillDown}
            onKeyDownCapture={captureArrowKeys}
        >
            <MuiMaterials.Select
                size="small"
                fullWidth
                value={value ?? ""}
                open={menuOpen}
                onOpen={() => setMenuOpen(true)}
                onClose={() => setMenuOpen(false)}
                onChange={(e) => onChange(e.target.value)}
                inputProps={cellId ? { "data-setup-cell": cellId } : undefined}
                sx={{
                    ...setupFieldWidthSx(sizeCh),
                    "& .MuiSelect-select": {
                        py: 0.4,
                        px: 0.75,
                        fontSize: 13,
                    },
                }}
                MenuProps={withScrollHintMenuProps({
                    PaperProps: {
                        className: "hide-scrollbar",
                        sx: HIDE_SCROLLBAR_SX,
                    },
                })}
            >
                {options.map((opt) => (
                    <MuiMaterials.MenuItem key={opt} value={opt}>
                        {opt}
                    </MuiMaterials.MenuItem>
                ))}
            </MuiMaterials.Select>
        </SetupCellWrap>
    );
}

function SetupCellInput({
    value,
    onChange,
    type = "text",
    sectionId,
    rowIndex,
    rowCount,
    columnKey,
    increment = false,
    onNavigate,
    onFillDown,
    showFillDownIcon = false,
    sizeCh,
}) {
    const border = getThemeColor("#cccccc", "#f4cc8e4d");
    const paper = getThemeColor("#ffffff", "#2d2d2d");
    const textPrimary = getThemeColor("#333333", "#f4cc8e");
    const cellId = sectionId != null && columnKey != null
        ? setupCellId(sectionId, rowIndex, columnKey)
        : undefined;
    const arrowEnabled = sectionId != null && onNavigate != null && rowCount != null;
    const showFillDown = showFillDownIcon && arrowEnabled && rowIndex < rowCount - 1;

    return (
        <SetupCellWrap
            showFillDown={showFillDown}
            onFillDown={onFillDown}
        >
            <input
                type={type === "number" ? "text" : type}
                inputMode={type === "number" ? "numeric" : undefined}
                data-setup-cell={cellId}
                value={value ?? ""}
                onChange={(e) => {
                    const raw = e.target.value;
                    onChange(type === "number" ? (raw === "" ? "" : Number(raw)) : raw);
                }}
                onKeyDown={arrowEnabled ? (e) => handleSetupArrowKeyDown(e, {
                    sectionId,
                    rowIndex,
                    rowCount,
                    columnKey,
                    increment,
                    valueType: type,
                    value,
                    onNavigate,
                }) : undefined}
                style={{
                    ...setupFieldWidthSx(sizeCh),
                    fontSize: 13,
                    padding: "4px 6px",
                    border: `1px solid ${border}`,
                    borderRadius: 4,
                    background: paper,
                    color: textPrimary,
                    boxSizing: "border-box",
                    outline: "none",
                }}
            />
        </SetupCellWrap>
    );
}

/** Per-slot CPS editor (BTS WpfMessage.ShowSectionSlots). */
function SectionCpsDialog({ open, section, sectionPositionRefs, onClose, onSave }) {
    const slots = effectiveSectionSlots(section, sectionPositionRefs);
    const defaultCps = Math.max(1, Number(section?.cablesPerSlot) || 1);
    const [values, setValues] = useState([]);

    useEffect(() => {
        if (!open || !section) return;
        const refs = (sectionPositionRefs ?? []).filter(
            (r) => r.sectionId === section.id
        );
        setValues(
            Array.from({ length: slots }, (_, i) => {
                const ref = refs.find((r) => r.sectionPosition === i + 1);
                return ref?.cablesPerSlot > 0 ? ref.cablesPerSlot : defaultCps;
            })
        );
    }, [open, section, sectionPositionRefs, slots, defaultCps]);

    if (!section) return null;

    const paper = getThemeColor("#ffffff", "#2d2d2d");
    const textPrimary = getThemeColor("#333333", "#f4cc8e");
    const border = getThemeColor("#cccccc", "#f4cc8e4d");

    return (
        <MuiMaterials.Modal
            open={open}
            onClose={onClose}
            aria-labelledby="section-cps-dialog-title"
        >
            <MuiMaterials.Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "100%",
                    maxWidth: 360,
                    outline: "none",
                }}
            >
                <MuiMaterials.Paper
                    elevation={8}
                    sx={{
                        p: 2,
                        backgroundColor: paper,
                        border: `1px solid ${border}`,
                    }}
                >
                    <MuiMaterials.Typography
                        id="section-cps-dialog-title"
                        variant="h6"
                        sx={{ mb: 2, fontWeight: 600, color: textPrimary }}
                    >
                        Cables per slot � {section.name}
                    </MuiMaterials.Typography>
                    {slots === 0 ? (
                        <MuiMaterials.Typography variant="body2" sx={{ py: 1, color: textPrimary }}>
                            Set Slots above 0 before editing per-slot CPS.
                        </MuiMaterials.Typography>
                    ) : (
                        <MuiMaterials.Stack spacing={1} sx={{ pt: 0, pb: 1 }}>
                            {values.map((val, i) => (
                                <MuiMaterials.Box
                                    key={`slot-cps-${i}`}
                                    sx={{ display: "flex", alignItems: "center", gap: 2 }}
                                >
                                    <MuiMaterials.Typography sx={{ minWidth: 48, color: textPrimary }}>
                                        Slot {i + 1}
                                    </MuiMaterials.Typography>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={val}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            const n = raw === "" ? "" : Number(raw);
                                            setValues((prev) => {
                                                const next = [...prev];
                                                next[i] = n;
                                                return next;
                                            });
                                        }}
                                        style={{
                                            flex: 1,
                                            padding: "6px 8px",
                                            fontSize: 13,
                                            border: `1px solid ${border}`,
                                            borderRadius: 4,
                                            background: paper,
                                            color: textPrimary,
                                        }}
                                    />
                                </MuiMaterials.Box>
                            ))}
                        </MuiMaterials.Stack>
                    )}
                    <MuiMaterials.Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        sx={{ mt: 2 }}
                    >
                        <MuiMaterials.Button size="small" variant="outlined" onClick={onClose}>Cancel</MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="contained"
                            disabled={slots === 0}
                            onClick={() => {
                                onSave(
                                    values.map((v, i) => ({
                                        sectionPosition: i + 1,
                                        cablesPerSlot: Math.max(0, Number(v) || 0),
                                    }))
                                );
                            }}
                        >
                            OK
                        </MuiMaterials.Button>
                    </MuiMaterials.Stack>
                </MuiMaterials.Paper>
            </MuiMaterials.Box>
        </MuiMaterials.Modal>
    );
}

/** Collects Customer + Site name for creating a brand-new BTS site. */
function NewSiteDialog({
    open,
    customerName,
    siteName,
    busy,
    customerOptions,
    siteNameOptions,
    optionsLoading,
    onCustomerNameChange,
    onSiteNameChange,
    onClose,
    onCreate,
}) {
    const paper = getThemeColor("#ffffff", "#2d2d2d");
    const textPrimary = getThemeColor("#333333", "#f4cc8e");
    const textSecondary = getThemeColor("#666666", "#c4a574");
    const border = getThemeColor("#cccccc", "#f4cc8e4d");
    const canCreate = customerName.trim().length > 0 && siteName.trim().length > 0 && !busy;
    const customerListId = "site-setup-customer-names";
    const siteListId = "site-setup-site-names";

    return (
        <MuiMaterials.Modal open={open} onClose={busy ? undefined : onClose} aria-labelledby="new-site-dialog-title">
            <MuiMaterials.Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "100%",
                    maxWidth: 420,
                    outline: "none",
                }}
            >
                <MuiMaterials.Paper elevation={8} sx={{ p: 3, backgroundColor: paper, border: `1px solid ${border}` }}>
                    <MuiMaterials.Typography
                        id="new-site-dialog-title"
                        variant="h6"
                        sx={{ mb: 1, fontWeight: 600, color: textPrimary }}
                    >
                        Create new site
                    </MuiMaterials.Typography>
                    <MuiMaterials.Typography variant="body2" sx={{ mb: 2, color: textSecondary }}>
                        A new BTS site database will be provisioned. Existing customers are reused by name.
                    </MuiMaterials.Typography>
                    <MuiMaterials.TextField
                        fullWidth
                        size="small"
                        label="Customer name"
                        value={customerName}
                        disabled={busy || optionsLoading}
                        onChange={(e) => onCustomerNameChange(e.target.value)}
                        sx={{ mb: 2 }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ list: customerListId, autoComplete: "off" }}
                        helperText={optionsLoading ? "Loading customers�" : "Choose an existing customer or type a new name."}
                    />
                    <datalist id={customerListId}>
                        {(customerOptions ?? []).map((name) => (
                            <option key={name} value={name} />
                        ))}
                    </datalist>
                    <MuiMaterials.TextField
                        fullWidth
                        size="small"
                        label="Site name"
                        value={siteName}
                        disabled={busy || optionsLoading}
                        onChange={(e) => onSiteNameChange(e.target.value)}
                        sx={{ mb: 1 }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ list: siteListId, autoComplete: "off" }}
                        helperText={
                            customerName.trim()
                                ? "Sites for this customer appear as suggestions; type a new name to add one."
                                : "All site names in the database appear as suggestions."
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && canCreate) onCreate();
                        }}
                    />
                    <datalist id={siteListId}>
                        {(siteNameOptions ?? []).map((name) => (
                            <option key={name} value={name} />
                        ))}
                    </datalist>
                    <MuiMaterials.Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                        <MuiMaterials.Button size="small" variant="outlined" onClick={onClose} disabled={busy}>Cancel</MuiMaterials.Button>
                        <MuiMaterials.Button size="small" variant="contained" disabled={!canCreate} onClick={onCreate}>
                            {busy ? "Creating�" : "Create"}
                        </MuiMaterials.Button>
                    </MuiMaterials.Stack>
                </MuiMaterials.Paper>
            </MuiMaterials.Box>
        </MuiMaterials.Modal>
    );
}

const SectionTable = memo(function SectionTable({
    title,
    sectionId,
    isOpen,
    onToggle,
    rows,
    columns,
    onAddAfter,
    onRemove,
    onUpdate,
    scrollContainerRef,
    scrollAnchorRef,
    addTooltip = "Add row below",
    removeTooltip = "Remove row",
    paginateRows = true,
    footer = null,
    toolbar = null,
}) {
    const sectionRef = useRef(null);
    const [contentReady, setContentReady] = useState(false);
    const [visibleCount, setVisibleCount] = useState(ROW_RENDER_CHUNK);
    const border = getThemeColor("#e0e0e0", "#3d3d3d");
    const paper = getThemeColor("#ffffff", "#2d2d2d");
    // Expanded form canvas (behind labels + inputs). Expander header stays on `paper`.
    const contentBg = getThemeColor("#e8e8e8", "#1e1e1e");
    const textSecondary = getThemeColor("#666666", "#c4a574");

    const rowCount = rows.length;
    const cappedRows = isOpen && contentReady
        ? (paginateRows ? rows.slice(0, visibleCount) : rows)
        : [];
    const hasMore = paginateRows && rowCount > cappedRows.length;
    const showActions = Boolean(onAddAfter || onRemove);

    const columnChByKey = useMemo(() => {
        const map = {};
        for (const col of columns) {
            map[col.key] = computeColumnSizeCh(col, rows);
        }
        return map;
    }, [columns, rows]);

    // Share the available width by the column `width` hints so the row never overflows.
    const columnWidthPctByKey = useMemo(() => {
        const weightOf = (col) => Math.max(1, Number(col.width) || columnChByKey[col.key] * 9 || 48);
        const totalWeight = columns.reduce((sum, col) => sum + weightOf(col), 0);
        // Leave a little headroom for action buttons (~7% for two 28px cols in a ~800px panel).
        const dataPct = showActions ? 93 : 100;
        const map = {};
        for (const col of columns) {
            map[col.key] = (weightOf(col) / totalWeight) * dataPct;
        }
        return map;
    }, [columns, columnChByKey, showActions]);

    const actionHeaderSx = {
        width: 28,
        minWidth: 28,
        maxWidth: 28,
        px: 0.25,
        textAlign: "center",
        fontWeight: 600,
        fontSize: 12,
    };

    const compactCellSx = {
        px: 0.5,
        py: 0.5,
    };

    const addButtonSx = {
        p: 0.25,
        minWidth: 24,
        width: 24,
        height: 24,
        bgcolor: getThemeColor("#4caf50", "#2e7d32"),
        color: "#fff",
        borderRadius: 0.5,
        "&:hover": { bgcolor: getThemeColor("#43a047", "#1b5e20") },
    };

    const removeButtonSx = {
        p: 0.25,
        minWidth: 24,
        width: 24,
        height: 24,
        bgcolor: getThemeColor("#f44336", "#c62828"),
        color: "#fff",
        borderRadius: 0.5,
        "&:hover": { bgcolor: getThemeColor("#e53935", "#b71c1c") },
    };

    const scrollSectionToTop = useCallback(() => {
        const container = scrollContainerRef?.current;
        const anchor = scrollAnchorRef?.current;
        const section = sectionRef.current;
        if (!container || !anchor || !section) return;

        const containerTop = container.getBoundingClientRect().top;
        const anchorTop = anchor.getBoundingClientRect().top;
        const sectionTop = section.getBoundingClientRect().top;
        const targetTop = Math.max(
            containerTop + SECTION_EXPAND_SCROLL_EXTRA,
            anchorTop + SECTION_EXPAND_SCROLL_EXTRA
        );
        container.scrollTop += sectionTop - targetTop;
    }, [scrollContainerRef, scrollAnchorRef]);

    const handleCellNavigate = useCallback((targetRow, columnKey, nextValue) => {
        if (targetRow < 0 || targetRow >= rows.length) return;
        if (targetRow >= visibleCount) {
            setVisibleCount((c) => Math.min(rows.length, targetRow + 1));
        }
        onUpdate(targetRow, columnKey, nextValue);
    }, [rows.length, visibleCount, onUpdate]);

    const handleFillDown = useCallback((fromRow, columnKey, fromValue, increment, valueType) => {
        if (fromRow >= rows.length - 1) return;
        if (paginateRows && rows.length > visibleCount) {
            setVisibleCount(rows.length);
        }
        let value = fromValue;
        for (let r = fromRow + 1; r < rows.length; r += 1) {
            value = increment
                ? copyValueForArrowNav(value, { increment: true, direction: "down" })
                : fromValue;
            onUpdate(r, columnKey, parseArrowNavValue(value, valueType));
        }
    }, [rows.length, visibleCount, paginateRows, onUpdate]);

    const arrowCellProps = useCallback((rowIndex, col, row) => {
        const canFill = rowIndex < rows.length - 1;
        const showIcon = canFill && columnShowsFillDownIcon(col);
        return {
            sectionId,
            rowIndex,
            rowCount: rows.length,
            columnKey: col.key,
            increment: !!col.increment,
            valueType: col.type,
            value: row[col.key],
            sizeCh: undefined,
            onNavigate: handleCellNavigate,
            showFillDownIcon: showIcon,
            onFillDown: canFill
                ? () => handleFillDown(
                    rowIndex,
                    col.key,
                    row[col.key],
                    !!col.increment,
                    col.type
                )
                : undefined,
        };
    }, [sectionId, rows.length, columnChByKey, handleCellNavigate, handleFillDown]);

    const handleToggle = useCallback(() => {
        onToggle?.(sectionId);
    }, [onToggle, sectionId]);

    useEffect(() => {
        if (!isOpen) {
            setContentReady(false);
            setVisibleCount(ROW_RENDER_CHUNK);
            return;
        }
        setContentReady(false);
        setVisibleCount(ROW_RENDER_CHUNK);
        requestAnimationFrame(() => {
            startTransition(() => setContentReady(true));
        });
    }, [isOpen, sectionId]);

    useEffect(() => {
        if (!isOpen) return undefined;
        let frame2 = 0;
        const frame1 = requestAnimationFrame(() => {
            frame2 = requestAnimationFrame(() => {
                scrollSectionToTop();
            });
        });
        return () => {
            cancelAnimationFrame(frame1);
            if (frame2) cancelAnimationFrame(frame2);
        };
    }, [isOpen, contentReady, scrollSectionToTop]);

    return (
        <MuiMaterials.Paper
            ref={sectionRef}
            elevation={0}
            className={`setup-section${isOpen ? " setup-section--open" : ""}`}
            sx={{
                backgroundColor: isOpen
                    ? contentBg
                    : "transparent",
                border: "none",
                borderRadius: 0,
                mb: 0,
                overflow: "hidden",
                boxShadow: "none",
            }}
        >
            <MuiMaterials.Box
                sx={{
                    px: { xs: 1.5, sm: 1.75 },
                    py: 1.25,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    userSelect: "none",
                }}
                onClick={handleToggle}
            >
                <MuiMaterials.ExpandMoreIcon
                    sx={{
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                    }}
                />
                <MuiMaterials.Typography fontWeight={600} sx={{ fontSize: { xs: 15, sm: 16 } }}>
                    {title}
                </MuiMaterials.Typography>
                <MuiMaterials.Typography variant="body2" sx={{ color: textSecondary }}>
                    ({rowCount})
                </MuiMaterials.Typography>
            </MuiMaterials.Box>
            {isOpen ? (
                <MuiMaterials.Box sx={{ px: 1, pb: 2, backgroundColor: contentBg }}>
                    {!contentReady ? (
                        <MuiMaterials.Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <MuiMaterials.RotatingLines
                                strokeColor={getThemeColor("#333333", "#f4cc8e")}
                                strokeWidth="3"
                                animationDuration="0.72"
                                width="48"
                                visible
                            />
                        </MuiMaterials.Box>
                    ) : (
                        <>
                            {toolbar ? (
                                <MuiMaterials.Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 1,
                                        px: 0.5,
                                        // Slightly more bottom pad so the bar sits visually centered
                                        // between the section header (py 1.25) and table head (py 0.75).
                                        pt: 1.25,
                                        pb: 1.75,
                                    }}
                                >
                                    {toolbar}
                                </MuiMaterials.Box>
                            ) : null}
                            <MuiMaterials.TableContainer
                                className="hide-scrollbar"
                                sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    overflowX: "hidden",
                                    backgroundColor: "transparent",
                                    ...HIDE_SCROLLBAR_SX,
                                }}
                            >
                                <MuiMaterials.Table
                                    size="small"
                                    stickyHeader
                                    sx={{
                                        width: "100%",
                                        tableLayout: "fixed",
                                        backgroundColor: "transparent",
                                        "& .MuiTableCell-head": {
                                            backgroundColor: contentBg,
                                        },
                                        "& .MuiTableCell-body": {
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                >
                                    <MuiMaterials.TableHead>
                                        <MuiMaterials.TableRow>
                                            {columns.map((col) => (
                                                <SetupColumnHeader
                                                    key={col.key}
                                                    label={col.label}
                                                    tooltip={col.tooltip}
                                                    minCh={columnChByKey[col.key]}
                                                    widthPct={columnWidthPctByKey[col.key]}
                                                    headerSx={col.headerSx}
                                                />
                                            ))}
                                            {showActions ? (
                                                <>
                                                    {onAddAfter ? (
                                                        <MuiMaterials.TableCell sx={actionHeaderSx} />
                                                    ) : null}
                                                    {onRemove ? (
                                                        <MuiMaterials.TableCell sx={actionHeaderSx} />
                                                    ) : null}
                                                </>
                                            ) : null}
                                        </MuiMaterials.TableRow>
                                    </MuiMaterials.TableHead>
                                    <MuiMaterials.TableBody>
                                        {rowCount === 0 && onAddAfter ? (
                                            <MuiMaterials.TableRow>
                                                <MuiMaterials.TableCell
                                                    colSpan={columns.length}
                                                    sx={{ ...compactCellSx, color: textSecondary, fontStyle: "italic" }}
                                                >
                                                    No rows
                                                </MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell sx={{ ...compactCellSx, ...actionHeaderSx }}>
                                                    <MuiMaterials.IconButton
                                                        size="small"
                                                        aria-label={addTooltip}
                                                        title={addTooltip}
                                                        sx={addButtonSx}
                                                        onClick={() => onAddAfter(-1)}
                                                    >
                                                        <AddIcon sx={{ fontSize: 14 }} />
                                                    </MuiMaterials.IconButton>
                                                </MuiMaterials.TableCell>
                                                {onRemove ? <MuiMaterials.TableCell sx={{ ...compactCellSx, ...actionHeaderSx }} /> : null}
                                            </MuiMaterials.TableRow>
                                        ) : null}
                                        {cappedRows.map((row, rowIndex) => (
                                            <MuiMaterials.TableRow key={rowIndex}>
                                                {columns.map((col) => (
                                                    <MuiMaterials.TableCell
                                                        key={col.key}
                                                        sx={setupDataCellSx(
                                                            columnChByKey[col.key],
                                                            columnWidthPctByKey[col.key]
                                                        )}
                                                    >
                                                        {col.render
                                                            ? col.render(
                                                                row,
                                                                (val) => onUpdate(rowIndex, col.key, val),
                                                                rowIndex,
                                                                arrowCellProps(rowIndex, col, row)
                                                            )
                                                            : (
                                                                <SetupCellInput
                                                                    value={row[col.key]}
                                                                    type={col.type}
                                                                    onChange={(val) => onUpdate(rowIndex, col.key, val)}
                                                                    {...arrowCellProps(rowIndex, col, row)}
                                                                />
                                                            )}
                                                    </MuiMaterials.TableCell>
                                                ))}
                                                {showActions ? (
                                                    <>
                                                        {onAddAfter ? (
                                                            <MuiMaterials.TableCell sx={{ ...compactCellSx, ...actionHeaderSx }}>
                                                                <MuiMaterials.IconButton
                                                                    size="small"
                                                                    aria-label={addTooltip}
                                                                    title={addTooltip}
                                                                    sx={addButtonSx}
                                                                    onClick={() => onAddAfter(rowIndex)}
                                                                >
                                                                    <AddIcon sx={{ fontSize: 14 }} />
                                                                </MuiMaterials.IconButton>
                                                            </MuiMaterials.TableCell>
                                                        ) : null}
                                                        {onRemove ? (
                                                            <MuiMaterials.TableCell sx={{ ...compactCellSx, ...actionHeaderSx }}>
                                                                <MuiMaterials.IconButton
                                                                    size="small"
                                                                    aria-label={removeTooltip}
                                                                    title={removeTooltip}
                                                                    sx={removeButtonSx}
                                                                    onClick={() => onRemove(rowIndex)}
                                                                >
                                                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                                                </MuiMaterials.IconButton>
                                                            </MuiMaterials.TableCell>
                                                        ) : null}
                                                    </>
                                                ) : null}
                                            </MuiMaterials.TableRow>
                                        ))}
                                    </MuiMaterials.TableBody>
                                </MuiMaterials.Table>
                            </MuiMaterials.TableContainer>
                            {hasMore ? (
                                <MuiMaterials.Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center" flexWrap="wrap">
                                    <MuiMaterials.Button
                                        size="small"
                                        onClick={() => setVisibleCount((c) => c + ROW_RENDER_CHUNK)}
                                    >
                                        Show more
                                    </MuiMaterials.Button>
                                    <MuiMaterials.Button
                                        size="small"
                                        onClick={() => startTransition(() => setVisibleCount(rowCount))}
                                    >
                                        Show all ({rowCount})
                                    </MuiMaterials.Button>
                                    <MuiMaterials.Typography variant="body2" sx={{ color: textSecondary }}>
                                        Showing {cappedRows.length} of {rowCount}
                                    </MuiMaterials.Typography>
                                </MuiMaterials.Stack>
                            ) : null}
                            {footer}
                        </>
                    )}
                </MuiMaterials.Box>
            ) : null}
        </MuiMaterials.Paper>
    );
});

const SiteSetupWindow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const loadAbortRef = useRef(null);
    const autoLoadHandledRef = useRef(false);
    const scrollContainerRef = useRef(null);
    const scrollAnchorRef = useRef(null);
    const [openSectionId, setOpenSectionId] = useState(null);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [sitesLoading, setSitesLoading] = useState(false);
    // Start loading when title-nav autoLoad carries a site so we never flash the site picker.
    const [setupLoading, setSetupLoading] = useState(() =>
        Boolean(location.state?.autoLoad && location.state?.siteGuid),
    );
    const [phase, setPhase] = useState("select");
    const [saving, setSaving] = useState(false);
    const [saveSetupConfirmOpen, setSaveSetupConfirmOpen] = useState(false);
    const [exportingBts, setExportingBts] = useState(false);
    // Set to the Device.uniqueId currently being re-pushed to ChirpStack so the Apply button on that row
    // disables itself (and shows "…"). Other rows stay enabled so parallel apply across many gateways works.
    const [heltecApplyingDeviceId, setHeltecApplyingDeviceId] = useState(null);
    const [pickerSiteGuid, setPickerSiteGuid] = useState(() =>
        location.state?.autoLoad && location.state?.siteGuid
            ? location.state.siteGuid
            : "",
    );
    const [loadedSiteGuid, setLoadedSiteGuid] = useState("");
    const [setup, setSetup] = useState(EMPTY_SETUP);
    const setupRef = useRef(setup);
    const [cpsDialogSectionId, setCpsDialogSectionId] = useState(null);
    const [status, setStatus] = useState("");
    const [statusDismissed, setStatusDismissed] = useState(false);
    const publishStatus = useCallback((message) => {
        setStatus(message);
        setStatusDismissed(!message);
    }, []);
    const [newSiteDialogOpen, setNewSiteDialogOpen] = useState(false);
    const [createSiteOptions, setCreateSiteOptions] = useState({ customers: [], sites: [] });
    const [createSiteOptionsLoading, setCreateSiteOptionsLoading] = useState(false);
    const [newCustomerName, setNewCustomerName] = useState("");
    const [newSiteName, setNewSiteName] = useState("");
    const [creatingSite, setCreatingSite] = useState(false);
    const [sensorForcedOpenOnly, setSensorForcedOpenOnly] = useState(false);
    const [sensorNameFilter, setSensorNameFilter] = useState("");

    useEffect(() => {
        setupRef.current = setup;
    }, [setup]);

    const { theme, textPrimary, textSecondary, paper, border, pageBg } = useMemo(
        () => createSiteSetupTheme(),
        [forceUpdate]
    );

    useEffect(() => {
        const onTheme = () => setForceUpdate((n) => n + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    useEffect(() => () => {
        loadAbortRef.current?.abort();
    }, []);

    // MUI controls and nested panels can swallow wheel events; scroll the main setup pane instead.
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el || phase !== "edit") return undefined;

        const canNestedScrollY = (node, deltaY) => {
            if (!(node instanceof HTMLElement)) return false;
            if (node.scrollHeight <= node.clientHeight + 1) return false;
            const overflowY = window.getComputedStyle(node).overflowY;
            if (overflowY !== "auto" && overflowY !== "scroll" && overflowY !== "overlay") {
                return false;
            }
            if (deltaY < 0) return node.scrollTop > 0;
            if (deltaY > 0) {
                return node.scrollTop + node.clientHeight < node.scrollHeight - 1;
            }
            return false;
        };

        const onWheel = (e) => {
            if (e.target.closest("[role='listbox'], .MuiMenu-list, .MuiPopover-root")) return;
            const { deltaY } = e;
            if (!deltaY) return;

            let node = e.target;
            while (node && node !== el) {
                if (canNestedScrollY(node, deltaY)) return;
                node = node.parentElement;
            }

            const maxTop = el.scrollHeight - el.clientHeight;
            if (maxTop <= 0) return;
            const next = Math.max(0, Math.min(maxTop, el.scrollTop + deltaY));
            if (next === el.scrollTop) return;
            el.scrollTop = next;
            e.preventDefault();
        };

        el.addEventListener("wheel", onWheel, { passive: false, capture: true });
        return () => el.removeEventListener("wheel", onWheel, { capture: true });
    }, [phase, openSectionId]);

    useEffect(() => {
        let cancelled = false;
        const hydrateSiteInfosFromResponse = (response) => {
            const sites = response?.data?.fetchUserAttribute?.sites;
            if (!Array.isArray(sites) || sites.length === 0) return;
            if ((global.gw?.siteInfos?.length ?? 0) > 0) return;
            global.gw.siteInfos = sites
                .map((site, siteIndex) => ({
                    siteIndex,
                    siteGuid: site.siteGuid,
                    siteName: site.main?.siteName ?? "Site",
                    lastScan: site.lastScan,
                    isHms: site.isHms,
                    bins: site.bins ?? [],
                }))
                .filter((s) => s.siteGuid && !s.isHms);
        };

        const loadSites = async () => {
            if ((global.gw?.siteInfos?.length ?? 0) > 0) {
                setForceUpdate((p) => p + 1);
                return;
            }
            if (!global.gw?.token) return;

            setSitesLoading(true);
            try {
                const response = await createApiEndpoint().fetchUserSites("direct");
                if (cancelled) return;
                hydrateSiteInfosFromResponse(response);
            } catch (err) {
                if (!cancelled) {
                    console.warn("SiteSetup: fetchUserSites", err);
                    publishStatus("Could not load site list. Try opening Customer Sites first.");
                }
            } finally {
                if (!cancelled) {
                    setSitesLoading(false);
                    setForceUpdate((p) => p + 1);
                }
            }
        };

        if (global.gw?.token) {
            loadSites();
        } else {
            let checks = 0;
            const waitForToken = setInterval(() => {
                checks += 1;
                if (cancelled) {
                    clearInterval(waitForToken);
                    return;
                }
                if (global.gw?.token) {
                    clearInterval(waitForToken);
                    loadSites();
                } else if (checks >= 50) {
                    clearInterval(waitForToken);
                    setSitesLoading(false);
                }
            }, 100);
            return () => {
                cancelled = true;
                clearInterval(waitForToken);
            };
        }

        return () => {
            cancelled = true;
        };
    }, []);

    const loadSetup = useCallback(async (guid) => {
        if (!guid) return;

        loadAbortRef.current?.abort();
        const controller = new AbortController();
        loadAbortRef.current = controller;

        setSetupLoading(true);
        publishStatus("");
        setSetup({ ...EMPTY_SETUP, taskSchedule: createEmptyTaskSchedule(), taskSelectedBins: [] });

        try {
            const result = await createApiEndpoint().fetchSiteSetup("direct", guid, controller.signal);
            if (controller.signal.aborted) return;

            if (!result?.ok) {
                publishStatus(result?.error || "Failed to load site setup.");
                setPhase("select");
                return;
            }

            const loaded = hydrateTaskSetupFromPayload(result.setup);
            setSetup({
                ...loaded,
                bins: updateBinFanCounts(loaded.bins, loaded.fans),
            });
            setLoadedSiteGuid(result.siteGuid || guid);
            setPickerSiteGuid(guid);
            setOpenSectionId(null);
            setSensorForcedOpenOnly(false);
            setSensorNameFilter("");
            setPhase("edit");
            publishStatus("");
        } catch (err) {
            if (controller.signal.aborted) return;
            console.error(err);
            publishStatus(`Load error: ${err.message}`);
            setPhase("select");
        } finally {
            if (loadAbortRef.current === controller) {
                setSetupLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        // Title-nav / site-card can re-enter /siteSetup with a new site while this page is mounted.
        if (autoLoadHandledRef.current === siteGuid) return;
        autoLoadHandledRef.current = siteGuid;
        setPickerSiteGuid(siteGuid);
        loadSetup(siteGuid);
    }, [location.state, loadSetup]);

    const handleContinueToSetup = useCallback((guid) => {
        const next = guid || pickerSiteGuid;
        if (!next) return;
        setPickerSiteGuid(next);
        if (next === NEW_SITE_OPTION) {
            publishStatus("");
            setNewSiteDialogOpen(true);
            return;
        }
        loadSetup(next);
    }, [pickerSiteGuid, loadSetup]);

    useEffect(() => {
        if (!newSiteDialogOpen || !global.gw?.token) return undefined;
        let cancelled = false;
        setCreateSiteOptionsLoading(true);
        createApiEndpoint()
            .fetchCreateSiteOptions("direct")
            .then((result) => {
                if (cancelled) return;
                setCreateSiteOptions({
                    customers: result?.customers ?? [],
                    sites: result?.sites ?? [],
                });
            })
            .catch((err) => {
                if (!cancelled) console.warn("SiteSetup: fetchCreateSiteOptions", err);
            })
            .finally(() => {
                if (!cancelled) setCreateSiteOptionsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [newSiteDialogOpen]);

    const createSiteCustomerOptions = useMemo(
        () => [...new Set((createSiteOptions.customers ?? []).map((n) => String(n).trim()).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
        [createSiteOptions.customers]
    );

    const createSiteNameOptions = useMemo(() => {
        const customer = newCustomerName.trim().toLowerCase();
        const rows = (createSiteOptions.sites ?? []).filter((row) => {
            if (!customer) return true;
            return String(row?.customerName ?? "").trim().toLowerCase() === customer;
        });
        return [...new Set(
            rows.map((row) => String(row?.siteName ?? "").trim()).filter(Boolean)
        )].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    }, [createSiteOptions.sites, newCustomerName]);

    const handleCreateNewSite = useCallback(async () => {
        const customerName = newCustomerName.trim();
        const siteName = newSiteName.trim();
        if (!customerName || !siteName) {
            publishStatus("Customer name and site name are required.");
            return;
        }
        setCreatingSite(true);
        publishStatus("");
        try {
            const result = await createApiEndpoint().createSite("direct", customerName, siteName);
            if (!result?.ok) {
                publishStatus(result?.error || "Failed to create site.");
                return;
            }

            const guid = result.siteGuid;
            const mainSiteName = result.mainSiteName || formatMainSiteName(customerName, siteName);
            if (!Array.isArray(global.gw.siteInfos)) global.gw.siteInfos = [];
            const infos = global.gw.siteInfos;
            const alreadyListed = infos.some(
                (s) => global.normalizeGuidForCompare(s.siteGuid) === global.normalizeGuidForCompare(guid)
            );
            if (!alreadyListed) {
                infos.push({
                    siteIndex: infos.length,
                    siteGuid: guid,
                    siteName: mainSiteName,
                    isHms: false,
                    bins: [],
                });
            }

            setNewSiteDialogOpen(false);
            setNewCustomerName("");
            setNewSiteName("");
            setSetup(createNewSiteSetup(mainSiteName));
            setLoadedSiteGuid(guid);
            setPickerSiteGuid(guid);
            setOpenSectionId(null);
            setPhase("edit");
            setForceUpdate((n) => n + 1);
            publishStatus("New site created. Configure the setup, then Save Setup to write devices, bins, and related tables to the BTS database.");
        } catch (err) {
            console.error("Create site error:", err);
            publishStatus(`Create error: ${err.message}`);
        } finally {
            setCreatingSite(false);
        }
    }, [newCustomerName, newSiteName]);

    const handleHeaderSiteChange = useCallback((guid) => {
        if (!guid) return;
        setPickerSiteGuid(guid);
        loadSetup(guid);
    }, [loadSetup]);

    const handleSectionToggle = useCallback((sectionId) => {
        setOpenSectionId((prev) => {
            if (prev === sectionId) return null;
            return sectionId;
        });
    }, []);

    useEffect(() => {
        if (!openSectionId) return;
        setSetup((prev) => refreshSetupSection(openSectionId, prev));
    }, [openSectionId]);

    useEffect(() => {
        if (openSectionId === "setupConfig") setSaveSetupConfirmOpen(false);
    }, [openSectionId]);

    const updateSetup = useCallback((patch) => {
        setSetup((prev) => ({ ...prev, ...patch }));
    }, []);

    const updateRow = useCallback((listKey, index, key, value) => {
        setSetup((prev) => {
            const list = [...(prev[listKey] ?? [])];
            list[index] = { ...list[index], [key]: value };
            return { ...prev, [listKey]: list };
        });
    }, []);

    const removeRow = useCallback((listKey, index) => {
        setSetup((prev) => {
            const list = [...(prev[listKey] ?? [])];
            list.splice(index, 1);
            return { ...prev, [listKey]: list };
        });
    }, []);

    const handleMainCountChange = useCallback((field, raw) => {
        const count = Math.max(0, parseInt(raw, 10) || 0);
        setSetup((prev) => {
            const main = { ...(prev.main ?? {}), [field]: count };
            if (field === "bins") {
                const bins = renumberBins(resizeList(prev.bins, count, (n, copy) => createDefaultBin(n, copy)));
                const { cables, sensors } = syncCablesFromBins(bins, prev.cables, prev.sections, prev.sensors);
                return {
                    ...prev,
                    main: { ...main, bins: count },
                    bins,
                    cables,
                    sensors,
                    fans: syncFansFromBins(bins, prev.fans, prev.devices),
                };
            }
            if (field === "devices") {
                const devices = renumberDevices(resizeList(prev.devices, count, (n, copy) => createDefaultDevice(n, copy)));
                const modules = syncModulesFromDevices(devices, prev.modules);
                const sectionSync = syncSectionsFromDevices(
                    devices,
                    prev.sections,
                    prev.sectionPositionRefs,
                    prev.cables,
                    prev.sensors
                );
                return {
                    ...prev,
                    main: { ...main, devices: count },
                    devices,
                    modules,
                    ...sectionSync,
                };
            }
            return { ...prev, main };
        });
    }, []);

    const handleDeviceUpdate = useCallback((index, key, value) => {
        setSetup((prev) => {
            const devices = [...prev.devices];
            if (!devices[index]) return prev;

            if (key === "modules" || key === "sections") {
                const count = Math.max(0, Number(value) || 0);
                devices[index] = { ...devices[index], [key]: count };
            } else if (key === "ipAddress") {
                const oldIp = devices[index].ipAddress;
                const deviceUniqueId = devices[index].uniqueId;
                devices[index] = { ...devices[index], ipAddress: value };
                const propagated = propagateDeviceIpAddress(
                    devices,
                    prev.modules,
                    prev.sections,
                    prev.sectionPositionRefs,
                    prev.cables,
                    prev.sensors,
                    prev.fans,
                    prev.bins,
                    deviceUniqueId,
                    oldIp
                );
                return { ...prev, devices, ...propagated };
            } else {
                devices[index] = { ...devices[index], [key]: value };
            }

            let next = { ...prev, devices };

            if (key === "modules") {
                next.modules = syncModulesFromDevices(devices, prev.modules);
            } else if (key === "sections") {
                const sectionSync = syncSectionsFromDevices(
                    devices,
                    prev.sections,
                    prev.sectionPositionRefs,
                    prev.cables,
                    prev.sensors
                );
                next = { ...next, ...sectionSync };
            }

            return next;
        });
    }, []);

    const handleApplyHeltecDevice = useCallback(async (device) => {
        if (!loadedSiteGuid || !device || !device.uniqueId) return;
        const eui = String(device.deviceEui ?? "").trim();
        if (!eui) {
            publishStatus(`Enter a gateway EUI for ${device.name} before applying Heltec config.`);
            return;
        }
        setHeltecApplyingDeviceId(device.uniqueId);
        publishStatus(`Applying Heltec config for ${device.name} (${eui})…`);
        try {
            // deviceUniqueId is the stable BTS Device.UniqueId; new rows use negative temp ids until Save
            // Setup assigns the real id. The server returns a JSON blob with ok / downlinkOk / message.
            const resultJson = await createApiEndpoint().applyHeltecDeviceConfig(
                "direct",
                loadedSiteGuid,
                device.uniqueId,
            );
            let parsed = null;
            try { parsed = resultJson ? JSON.parse(resultJson) : null; } catch { parsed = null; }
            if (parsed?.ok) {
                const dl = parsed.downlinkOk ? "downlink queued" : "downlink FAILED (gateway will pull on next poll)";
                publishStatus(
                    `Heltec config applied for ${device.name}: ${parsed.mappingRowsWritten || 0} mapping rows, `
                    + `${dl}.${parsed.message ? ` ${parsed.message}` : ""}`
                );
            } else {
                publishStatus(parsed?.message || `Failed to apply Heltec config for ${device.name}.`);
            }
        } catch (err) {
            publishStatus(`Heltec apply error: ${err?.message || err}`);
        } finally {
            setHeltecApplyingDeviceId(null);
        }
    }, [loadedSiteGuid, publishStatus]);

    const handleRemoveDevice = useCallback((index) => {
        setSetup((prev) => {
            const devices = renumberDevices(prev.devices.filter((_, i) => i !== index));
            const modules = syncModulesFromDevices(devices, prev.modules);
            const sectionSync = syncSectionsFromDevices(
                devices,
                prev.sections,
                prev.sectionPositionRefs,
                prev.cables,
                prev.sensors
            );
            return {
                ...prev,
                devices,
                modules,
                ...sectionSync,
                main: prev.main ? { ...prev.main, devices: devices.length } : prev.main,
            };
        });
    }, []);

    const handleBinUpdate = useCallback((index, key, value) => {
        setSetup((prev) => {
            let bins = [...prev.bins];
            if (key === "cableLayout") {
                const bin = bins[index];
                const fromLayout = cableCountFromLayout(value, bin);
                bins[index] = {
                    ...bins[index],
                    cableLayout: value,
                    ...(fromLayout > 0 ? { cables: fromLayout } : {}),
                };
            } else {
                bins[index] = { ...bins[index], [key]: value };
            }
            if (key === "cables") {
                bins[index] = {
                    ...bins[index],
                    cableLayout: figureBinCableLayout(bins[index]),
                };
            }
            if (key === "cables" || key === "cableLayout") {
                bins = applyBinCableLayouts(bins);
                const { cables, sensors } = syncCablesFromBins(bins, prev.cables, prev.sections, prev.sensors);
                return { ...prev, bins, cables, sensors };
            }
            if (key === "fans") {
                return { ...prev, bins, fans: syncFansFromBins(bins, prev.fans, prev.devices) };
            }
            return { ...prev, bins };
        });
    }, []);

    const handleRemoveBin = useCallback((index) => {
        setSetup((prev) => {
            const bins = renumberBins(prev.bins.filter((_, i) => i !== index));
            const { cables, sensors } = syncCablesFromBins(bins, prev.cables, prev.sections, prev.sensors);
            return {
                ...prev,
                bins,
                cables,
                sensors,
                fans: syncFansFromBins(bins, prev.fans, prev.devices),
                main: prev.main ? { ...prev.main, bins: bins.length } : prev.main,
            };
        });
    }, []);

    const handleCableUpdate = useCallback((index, key, value) => {
        setSetup((prev) => {
            const cables = [...prev.cables];
            cables[index] = { ...cables[index], [key]: value };
            if (key === "sensorCount" || key === "sensorSpacing") {
                return {
                    ...prev,
                    cables,
                    sensors: syncSensorsFromCables(cables, prev.sensors, prev.bins),
                };
            }
            return { ...prev, cables };
        });
    }, []);

    const handleRemoveCable = useCallback((index) => {
        setSetup((prev) => {
            const removed = prev.cables[index];
            const cables = prev.cables.filter((_, i) => i !== index);
            const sensors = prev.sensors.filter((s) => s.cableId !== removed?.id);
            const bins = updateBinCableCounts(prev.bins, cables, sensors);
            const { cables: synced, sensors: syncedSensors } = syncCablesFromBins(
                bins,
                cables,
                prev.sections,
                sensors
            );
            return { ...prev, bins, cables: synced, sensors: syncedSensors };
        });
    }, []);

    const handleRemoveFan = useCallback((index) => {
        setSetup((prev) => {
            const fans = prev.fans.filter((_, i) => i !== index);
            const bins = updateBinFanCounts(prev.bins, fans);
            return { ...prev, bins, fans: syncFansFromBins(bins, fans, prev.devices) };
        });
    }, []);

    const handleModuleUpdate = useCallback((index, key, value) => {
        setSetup((prev) => {
            const modules = [...prev.modules];
            const row = modules[index];
            if (!row) return prev;

            if (key === "moduleType") {
                const device = prev.devices.find((d) => d.uniqueId === row.deviceUniqueId)
                    ?? prev.devices.find((d) => d.ipAddress === row.ipAddress);
                modules[index] = applyModuleTypeChange(row, value, device?.type);
            } else if (key === "type") {
                modules[index] = { ...row, type: value };
            } else {
                modules[index] = { ...row, [key]: value };
            }
            return { ...prev, modules };
        });
    }, []);

    const handleSectionUpdate = useCallback((index, key, value) => {
        setSetup((prev) => {
            const sections = [...prev.sections];
            const section = { ...sections[index] };

            if (key === "slots" || key === "slotsUsed") {
                const slotsVal = Math.max(0, Number(value) || 0);
                section.slotsUsed = slotsVal;
                section.slots = slotsVal;
                sections[index] = section;
                let sectionPositionRefs = syncSectionPositionRefsForSection(
                    section,
                    prev.sectionPositionRefs ?? []
                );
                sectionPositionRefs = sortSectionPositionRefs(
                    sections,
                    sectionPositionRefs
                );
                return { ...prev, sections, sectionPositionRefs };
            }

            if (key === "cablesPerSlot") {
                section.cablesPerSlot = Math.max(1, Number(value) || 1);
                sections[index] = section;
                let sectionPositionRefs = applySectionCablesPerSlotToRefs(
                    section.id,
                    section.cablesPerSlot,
                    prev.sectionPositionRefs ?? []
                );
                if (
                    !(prev.sectionPositionRefs ?? []).some(
                        (r) => r.sectionId === section.id
                    )
                ) {
                    sectionPositionRefs = syncSectionPositionRefsForSection(
                        section,
                        sectionPositionRefs
                    );
                }
                sectionPositionRefs = sortSectionPositionRefs(
                    sections,
                    sectionPositionRefs
                );
                return { ...prev, sections, sectionPositionRefs };
            }

            sections[index] = { ...section, [key]: value };
            return { ...prev, sections };
        });
    }, []);

    const handleRemoveSection = useCallback((index) => {
        setSetup((prev) => {
            const removed = prev.sections[index];
            const sections = prev.sections
                .filter((_, i) => i !== index)
                .map((s, idx) => ({ ...s, number: idx + 1 }));
            const sectionPositionRefs = (prev.sectionPositionRefs ?? []).filter(
                (r) => r.sectionId !== removed?.id
            );
            return { ...prev, sections, sectionPositionRefs };
        });
    }, []);

    const handleSaveSectionCps = useCallback((slotValues) => {
        setSetup((prev) => {
            const section = prev.sections.find((s) => s.id === cpsDialogSectionId);
            if (!section) return prev;

            let sectionPositionRefs = [...(prev.sectionPositionRefs ?? [])];
            for (const { sectionPosition, cablesPerSlot } of slotValues) {
                const cps = Math.max(1, Number(cablesPerSlot) || 1);
                const existing = sectionPositionRefs.find(
                    (r) =>
                        r.sectionId === section.id &&
                        r.sectionPosition === sectionPosition
                );
                if (existing) {
                    existing.cablesPerSlot = cps;
                } else {
                    sectionPositionRefs.push({
                        id: nextTempSetupId(sectionPositionRefs),
                        sectionId: section.id,
                        sectionPosition,
                        cablesPerSlot: cps,
                    });
                }
            }
            sectionPositionRefs = sortSectionPositionRefs(
                prev.sections,
                sectionPositionRefs
            );
            return { ...prev, sectionPositionRefs };
        });
        setCpsDialogSectionId(null);
    }, [cpsDialogSectionId]);

    const handleAssignSections = useCallback(() => {
        setSetup((prev) => {
            const result = assignSectionsToCables(
                prev.sections,
                prev.sectionPositionRefs,
                prev.bins,
                prev.cables,
                prev.sensors
            );
            const total = result.cables.length;
            const assigned = result.cables.filter(
                (c) => (Number(c.sectionId) || 0) > 0 || Boolean(c.sectionName)
            ).length;
            const capacity = totalSectionAssignCapacity(
                result.sections ?? prev.sections,
                result.sectionPositionRefs
            );
            if (assigned === 0) {
                publishStatus("No section slots available � set Slots on each section, then try again.");
            } else if (assigned === total) {
                publishStatus(`Sections assigned to all ${assigned} cables.`);
            } else {
                publishStatus(
                    `Sections assigned to ${assigned} of ${total} cables `
                    + `(${capacity} section slot${capacity === 1 ? "" : "s"} available).`
                );
            }
            return {
                ...prev,
                sections: result.sections ?? prev.sections,
                sectionPositionRefs: result.sectionPositionRefs,
                cables: result.cables,
                sensors: result.sensors,
            };
        });
    }, []);

    const handleSave = async () => {
        if (!loadedSiteGuid || !setupRef.current?.main) return;
        setSaving(true);
        publishStatus("");
        try {
            const normalized = prepareSetupForSave(setupRef.current);
            const payload = {
                main: {
                    ...prepareMainForSave(normalized.main),
                    bins: normalized.bins.length,
                    devices: normalized.devices.length,
                },
                devices: normalized.devices,
                modules: normalized.modules,
                sections: normalized.sections,
                sectionPositionRefs: normalized.sectionPositionRefs,
                bins: normalized.bins,
                fans: normalized.fans,
                cables: normalized.cables,
                sensors: normalized.sensors,
                tasks: normalized.tasks,
            };
            const result = await createApiEndpoint().saveSiteSetup("direct", loadedSiteGuid, payload);
            if (result === "OK") {
                setSetup(normalized);
                await loadSetup(loadedSiteGuid);
                publishStatus("Site setup saved. This will sync to the customer's local BTS database if it exists, has the latest Temputech Service, and is online.");
            } else {
                console.error("Site setup save failed:", result);
                publishStatus(result || "Save failed.");
            }
        } catch (err) {
            const detail = err?.stack ? `${err.message}\n${err.stack}` : String(err);
            console.error("Site setup save error:", detail);
            publishStatus(`Save error: ${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const handleExportBtsDatabase = async () => {
        if (!loadedSiteGuid || exportingBts) return;
        setExportingBts(true);
        publishStatus("Preparing BTS.mdf / BTS_log.ldf export�");
        try {
            const result = await createApiEndpoint().exportSiteSetupBtsMdf("direct", loadedSiteGuid);
            if (!result?.ok) {
                publishStatus(result?.error || "Failed to export BTS database.");
                return;
            }
            const zipBytes = global.base64StringToArrayBuffer(result.zipBase64);
            const blob = new Blob([zipBytes], { type: "application/zip" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = result.fileName || "BTS.zip";
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            const sizeMb = result.sizeBytes
                ? ` (${(result.sizeBytes / (1024 * 1024)).toFixed(1)} MB)`
                : "";
            publishStatus(`Downloaded ${result.fileName || "BTS.zip"}${sizeMb}. Zip contains BTS.mdf and BTS_log.ldf for MSSQL attach or gateway deployment.`);
        } catch (err) {
            const detail = err?.stack ? `${err.message}\n${err.stack}` : String(err);
            console.error("Site setup BTS export error:", detail);
            publishStatus(`Export error: ${err.message || detail}`);
        } finally {
            setExportingBts(false);
        }
    };

    const deviceIpOptions = useMemo(
        () => setup.devices.map((d) => d.ipAddress).filter(Boolean),
        [setup.devices]
    );
    const sectionNameOptions = useMemo(
        () => setup.sections.map((s) => s.name).filter(Boolean),
        [setup.sections]
    );
    const cableNameOptions = useMemo(
        () => setup.cables.map((c) => c.name).filter(Boolean),
        [setup.cables]
    );

    const sensorFilterActive = sensorForcedOpenOnly || Boolean(sensorNameFilter.trim());
    const filteredSensorRows = useMemo(() => {
        const nameQuery = sensorNameFilter.trim().toLowerCase();
        const indices = [];
        const rows = [];
        (setup.sensors ?? []).forEach((sensor, index) => {
            if (sensorForcedOpenOnly && !sensor.forcedOpen) return;
            if (nameQuery) {
                const name = String(sensor.name ?? "").toLowerCase();
                const deviceIp = String(sensor.deviceIp ?? sensor.DeviceIp ?? "").toLowerCase();
                if (!name.includes(nameQuery) && !deviceIp.includes(nameQuery)) return;
            }
            indices.push(index);
            rows.push(sensor);
        });
        return { rows, indices };
    }, [setup.sensors, sensorForcedOpenOnly, sensorNameFilter]);

    const mapSensorRowIndex = useCallback(
        (filteredIndex) => {
            if (filteredIndex < 0) return filteredIndex;
            if (!sensorFilterActive) return filteredIndex;
            return filteredSensorRows.indices[filteredIndex] ?? filteredIndex;
        },
        [sensorFilterActive, filteredSensorRows.indices]
    );

    const cpsDialogSection = useMemo(
        () => setup.sections.find((s) => s.id === cpsDialogSectionId) ?? null,
        [setup.sections, cpsDialogSectionId]
    );

    const mainRow = setup.main
        ? [{
            siteName: setup.main.siteName ?? "",
            bins: setup.bins.length,
            devices: setup.devices.length,
        }]
        : [];

    const comparisonReportEmails = mainSetupEmailValue(setup.main, "emailReportTo", "EmailReportTo");
    const alertEmails = mainSetupEmailValue(setup.main, "emailAlertTo", "EmailAlertTo");
    const screenshotEmails = mainSetupEmailValue(setup.main, "emailScreenshotTo", "EmailScreenshotTo");
    const lastTemperatureEmails = mainSetupEmailValue(
        setup.main,
        "emailLastTemperatureReportTo",
        "EmailLastTemperatureReportTo"
    );

    const updateMainEmail = useCallback((key, value) => {
        setSetup((prev) => ({
            ...prev,
            main: { ...prev.main, [key]: value },
        }));
    }, []);

    const handleAutoScanChange = useCallback((isOn) => {
        setSetup((prev) => ({
            ...prev,
            main: { ...prev.main, isAutoScanOn: isOn },
        }));
    }, []);

    const handleTaskBinsChange = useCallback((binNames) => {
        setSetup((prev) => ({ ...prev, taskSelectedBins: binNames }));
    }, []);

    const handleTaskSlotChange = useCallback((day, time, actionKey, checked) => {
        setSetup((prev) => ({
            ...prev,
            taskSchedule: {
                ...prev.taskSchedule,
                [day]: {
                    ...prev.taskSchedule?.[day],
                    [time]: {
                        ...(prev.taskSchedule?.[day]?.[time] ?? {}),
                        [actionKey]: checked,
                    },
                },
            },
        }));
    }, []);

    const taskBinNames = useMemo(
        () => setup.bins.map((b) => b.name).filter(Boolean),
        [setup.bins]
    );

    const isAutoScanOn = !!(setup.main?.isAutoScanOn ?? setup.main?.IsAutoScanOn);

    return (
        <MuiMaterials.ThemeProvider theme={theme}>
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    background: pageBg,
                    color: textPrimary,
                    paddingTop: "env(safe-area-inset-top, 0px)",
                    paddingBottom: "env(safe-area-inset-bottom, 0px)",
                }}
            >
                {(setupLoading || saving) ? (
                    <LoadingOverlay forceUpdate={forceUpdate} />
                ) : null}

                <ChromeHeaderBar
                    paper={paper}
                    border={border}
                    textSecondary={textSecondary}
                    onClose={() => navigate("/sites", { replace: true })}
                    leading={
                        phase === "edit" ? (
                            <SiteWindowHeaderLeading
                                toolTitle="Site Setup"
                                siteWindowId={SITE_WINDOW_IDS.setup}
                                siteGuid={loadedSiteGuid || pickerSiteGuid || null}
                                textSecondary={textSecondary}
                                border={border}
                                showSiteSelect
                                siteSelectValue={loadedSiteGuid}
                                onSiteChange={handleHeaderSiteChange}
                                filterSite={(s) => !s.isHms}
                                siteSelectDisabled={setupLoading || saving || exportingBts}
                                includeTool={false}
                            />
                        ) : null
                    }
                    tool={
                        <SiteWindowToolNav
                            toolTitle="Site Setup"
                            siteWindowId={SITE_WINDOW_IDS.setup}
                            siteGuid={loadedSiteGuid || pickerSiteGuid || null}
                            textSecondary={textSecondary}
                        />
                    }
                    actions={
                        phase === "edit" ? (
                            <>
                                <MuiMaterials.Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    disabled={!loadedSiteGuid || exportingBts || saving}
                                    onClick={handleExportBtsDatabase}
                                    title="Download BTS.mdf and BTS_log.ldf in a zip for MSSQL attach or gateway deployment"
                                >
                                    {exportingBts ? "Exporting…" : "Download BTS"}
                                </MuiMaterials.Button>
                                <MuiMaterials.Tooltip
                                    title={
                                        openSectionId === "setupConfig"
                                            ? "Config is open. Use Save Config in that section. Close Config to save site setup."
                                            : "Saves devices, bins, cables, and the other setup sections. Does not save Config."
                                    }
                                >
                                    <span style={{ display: "inline-flex" }}>
                                        <MuiMaterials.Button
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            disabled={
                                                openSectionId === "setupConfig"
                                                || !setup.main
                                                || saving
                                                || exportingBts
                                            }
                                            onClick={() => setSaveSetupConfirmOpen(true)}
                                        >
                                            Save Setup
                                        </MuiMaterials.Button>
                                    </span>
                                </MuiMaterials.Tooltip>
                            </>
                        ) : null
                    }
                />


                {status && !statusDismissed ? (
                    <MuiMaterials.Box
                        sx={{
                            mx: 2,
                            mt: 1,
                            flexShrink: 0,
                            p: 1.5,
                            borderRadius: 1,
                            border: `1px solid ${border}`,
                            backgroundColor: getThemeColor("#fff8e1", "#3a3228"),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                        }}
                    >
                        <MuiMaterials.Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", flex: 1, m: 0 }}
                        >
                            {status}
                        </MuiMaterials.Typography>
                        <MuiMaterials.Button
                            variant="outlined"
                            onClick={() => setStatusDismissed(true)}
                            sx={{
                                flexShrink: 0,
                                minWidth: 56,
                                p: "10px",
                                lineHeight: 1,
                            }}
                        >
                            OK
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                ) : null}

                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={handleContinueToSetup}
                        onCancel={() => navigate("/sites", { replace: true })}
                        loading={sitesLoading || setupLoading}
                        filterSite={(s) => !s.isHms}
                        allowCreateNewSite
                    />
                ) : null}

                {phase === "edit" ? (
                <MuiMaterials.Box
                    ref={scrollContainerRef}
                    className="hide-scrollbar"
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        px: { xs: 1, sm: 1.5, md: 2 },
                        pt: 1.5,
                        pb: 2,
                        boxSizing: "border-box",
                        ...HIDE_SCROLLBAR_SX,
                    }}
                >
                        <MuiMaterials.Box
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                mx: "auto",
                                boxSizing: "border-box",
                                p: { xs: "8px 10px", sm: "12px 14px" },
                                borderRadius: "12px",
                                border: `1px solid ${getThemeColor("rgba(0, 0, 0, 0.12)", "rgba(244, 204, 142, 0.28)")}`,
                                background: getThemeColor("#f8f8f8", "#313131"),
                                boxShadow: getThemeColor(
                                    "0 2px 8px rgba(0, 0, 0, 0.04)",
                                    "0 4px 16px rgba(0, 0, 0, 0.2)",
                                ),
                            }}
                        >
                        <MuiMaterials.Box
                            ref={scrollAnchorRef}
                            className="setup-sections-list"
                            sx={{
                                width: "100%",
                                borderRadius: "8px",
                                border: `1px solid ${getThemeColor("#d8d8d8", "#3d3d3d")}`,
                                background: getThemeColor("#ffffff", "#2a2a2a"),
                                boxShadow: getThemeColor(
                                    "inset 0 1px 0 rgba(255,255,255,0.8)",
                                    "inset 0 1px 0 rgba(255,255,255,0.04)",
                                ),
                                overflow: "hidden",
                                "& > .setup-section": {
                                    borderBottom: `1px solid ${getThemeColor("rgba(0, 0, 0, 0.06)", "rgba(255, 255, 255, 0.06)")}`,
                                },
                                "& > .setup-section:last-child": {
                                    borderBottom: "none",
                                },
                                "& > .setup-section:nth-of-type(even):not(.setup-section--open)": {
                                    background: getThemeColor("rgba(0, 0, 0, 0.02)", "rgba(255, 255, 255, 0.02)"),
                                },
                                "& > .setup-section:not(.setup-section--open):hover": {
                                    background: getThemeColor("rgba(0, 0, 0, 0.025)", "rgba(244, 204, 142, 0.05)"),
                                },
                                "& > .setup-section.setup-section--open, & > .setup-section.setup-section--open:nth-of-type(even), & > .setup-section.setup-section--open:hover, & > .setup-section.setup-section--open:nth-of-type(even):hover": {
                                    background: `${getThemeColor("#e8e8e8", "#1e1e1e")} !important`,
                                },
                            }}
                        >
                            <SectionTable
                                title="Main"
                                sectionId="main"
                                isOpen={openSectionId === "main"}
                                onToggle={handleSectionToggle}
                                scrollContainerRef={scrollContainerRef}
                                scrollAnchorRef={scrollAnchorRef}
                                rows={mainRow}
                                columns={[
                                    {
                                        key: "siteName",
                                        label: "Site Name",
                                        render: (row, onChange) => (
                                            <SetupTextField
                                                value={row.siteName}
                                                onChange={(val) => {
                                                    updateSetup({ main: { ...setup.main, siteName: val } });
                                                    onChange(val);
                                                }}
                                            />
                                        ),
                                    },
                                    {
                                        key: "bins",
                                        label: "Bins",
                                        render: (row) => (
                                            <SetupTextField
                                                value={row.bins}
                                                type="number"
                                                onChange={(val) => handleMainCountChange("bins", val)}
                                            />
                                        ),
                                    },
                                    {
                                        key: "devices",
                                        label: "Devices",
                                        render: (row) => (
                                            <SetupTextField
                                                value={row.devices}
                                                type="number"
                                                onChange={(val) => handleMainCountChange("devices", val)}
                                            />
                                        ),
                                    },
                                ]}
                                footer={(
                                    <MuiMaterials.Box>
                                        <MainSetupEmailArea
                                            label="Comparison Report Emails"
                                            tooltip="Comma or semicolon-separated email addresses for comparison and GMS reports (Main.EmailReportTo)."
                                            value={comparisonReportEmails}
                                            onChange={(val) => updateMainEmail("emailReportTo", val)}
                                        />
                                        <MainSetupEmailArea
                                            label="Alert Emails"
                                            tooltip="Comma or semicolon-separated email addresses for fan on/off notifications and alerts (Main.EmailAlertTo)."
                                            value={alertEmails}
                                            onChange={(val) => updateMainEmail("emailAlertTo", val)}
                                        />
                                        <MainSetupEmailArea
                                            label="Screenshot Emails"
                                            tooltip="Comma or semicolon-separated email addresses for scan screenshots (Main.EmailScreenshotTo)."
                                            value={screenshotEmails}
                                            onChange={(val) => updateMainEmail("emailScreenshotTo", val)}
                                        />
                                        <MainSetupEmailArea
                                            label="Last Temperature Emails"
                                            tooltip="Comma or semicolon-separated email addresses for the last temperatures CSV report (Main.EmailLastTemperatureReportTo)."
                                            value={lastTemperatureEmails}
                                            onChange={(val) => updateMainEmail("emailLastTemperatureReportTo", val)}
                                        />
                                    </MuiMaterials.Box>
                                )}
                                onUpdate={() => {}}
                            />

                            <SectionTable
                                title="Device"
                                sectionId="devices"
                                isOpen={openSectionId === "devices"}
                                onToggle={handleSectionToggle}
                                scrollContainerRef={scrollContainerRef}
                                scrollAnchorRef={scrollAnchorRef}
                                rows={setup.devices}
                                columns={[
                                    {
                                        key: "model",
                                        label: "Model",
                                        width: 90,
                                        tooltip: "Device.Model — saved to the site database and the local BTS",
                                    },
                                    {
                                        key: "name",
                                        label: "Name",
                                        increment: true,
                                        width: 90,
                                        tooltip: "Device.Name — saved to the site database and the local BTS",
                                    },
                                    {
                                        key: "type",
                                        label: "Type",
                                        measureValues: DEVICE_TYPES,
                                        width: 90,
                                        render: (row, onChange, rowIndex, arrow) => (
                                            <SetupSelect value={row.type} options={DEVICE_TYPES} onChange={onChange} {...arrow} />
                                        ),
                                    },
                                    { key: "ipAddress", label: "IP", width: 72 },
                                    {
                                        // Heltec gateway EUI (nullable Device.DeviceEui). The server rebuilds
                                        // HeltecSensorMapping, merges the per-device block into SiteConfig, and
                                        // queues a ChirpStack apply_config downlink using this value.
                                        // Always editable regardless of Type so admins can paste the EUI before
                                        // flipping Type → Heltec; the value is simply ignored until Type = Heltec.
                                        key: "deviceEui",
                                        label: "EUI",
                                        width: 140,
                                        tooltip: "Heltec gateway EUI (16 hex chars, e.g. 0604a7ac0000d0be). Only used when Type = Heltec; stored but ignored for every other device type.",
                                        render: (row, onChange) => (
                                            <MuiMaterials.TextField
                                                size="small"
                                                value={row.deviceEui ?? ""}
                                                onChange={(e) => onChange(e.target.value)}
                                                placeholder="0604a7ac0000d0be"
                                                inputProps={{
                                                    maxLength: 64,
                                                    style: { fontFamily: "monospace", fontSize: 12 },
                                                }}
                                            />
                                        ),
                                    },
                                    {
                                        // Force a re-push without saving Site Setup (useful after a chirpstack
                                        // outage). Save Setup already triggers this for every Heltec device.
                                        // Always rendered so the column stays discoverable even when Type ≠ Heltec;
                                        // disabled with a dynamic tooltip explaining what's missing.
                                        key: "heltecApply",
                                        label: "",
                                        width: 72,
                                        tooltip: "Re-apply Heltec config: rebuild HeltecSensorMapping, update SiteConfig heltecDevices[eui], and queue a ChirpStack apply_config downlink for this gateway. Requires Type = Heltec and a non-empty EUI.",
                                        render: (row) => {
                                            const isHeltec = String(row.type ?? "").toLowerCase() === "heltec";
                                            const hasEui = String(row.deviceEui ?? "").trim().length > 0;
                                            const isBusy = heltecApplyingDeviceId === row.uniqueId;
                                            const disabledReason = !isHeltec
                                                ? "Set Type to Heltec first."
                                                : !hasEui
                                                    ? "Enter the gateway EUI first."
                                                    : isBusy
                                                        ? "Applying…"
                                                        : "Re-apply Heltec config and queue downlink.";
                                            return (
                                                <MuiMaterials.Tooltip title={disabledReason} arrow>
                                                    {/* span wrapper lets Tooltip show on a disabled button */}
                                                    <span>
                                                        <MuiMaterials.Button
                                                            size="small"
                                                            variant="outlined"
                                                            disabled={!isHeltec || !hasEui || isBusy}
                                                            onClick={() => handleApplyHeltecDevice(row)}
                                                            sx={{ minWidth: 56, py: 0.25, fontSize: 11 }}
                                                        >
                                                            {isBusy ? "…" : "Apply"}
                                                        </MuiMaterials.Button>
                                                    </span>
                                                </MuiMaterials.Tooltip>
                                            );
                                        },
                                    },
                                    { key: "modules", label: "Modules", type: "number", width: 56 },
                                    { key: "sections", label: "Sections", type: "number", width: 56 },
                                    {
                                        key: "hasPowerReset",
                                        label: "Power Reset",
                                        width: 72,
                                        tooltip: "Check if this device has a power reset.",
                                        render: (row, onChange) => <SetupCheckbox checked={row.hasPowerReset} onChange={onChange} />,
                                    },
                                    {
                                        key: "hasResistanceCheck",
                                        label: "Resistance",
                                        width: 72,
                                        tooltip: "Check if this device can check resistance.",
                                        render: (row, onChange) => <SetupCheckbox checked={row.hasResistanceCheck} onChange={onChange} />,
                                    },
                                    {
                                        key: "hasHumiditySensor",
                                        label: "Humidity",
                                        width: 64,
                                        tooltip: "Check if this device has a relative humidity sensor.",
                                        render: (row, onChange) => <SetupCheckbox checked={row.hasHumiditySensor} onChange={onChange} />,
                                    },
                                    {
                                        key: "isAmbientDevice",
                                        label: "AD",
                                        width: 40,
                                        tooltip: "Check if this device is the device that checks the ambient temperature.",
                                        render: (row, onChange) => <SetupCheckbox checked={row.isAmbientDevice} onChange={onChange} />,
                                    },
                                    {
                                        key: "isHumidityDevice",
                                        label: "HD",
                                        width: 40,
                                        tooltip: "Check if this device is the device that checks the relative humidity.",
                                        render: (row, onChange) => <SetupCheckbox checked={row.isHumidityDevice} onChange={onChange} />,
                                    },
                                ]}
                                onAddAfter={(i) => {
                                    setSetup((prev) => {
                                        const devices = [...prev.devices];
                                        devices.splice(i + 1, 0, createDefaultDevice(devices.length + 1, devices[i]));
                                        const renumbered = renumberDevices(devices);
                                        return {
                                            ...prev,
                                            devices: renumbered,
                                            main: prev.main ? { ...prev.main, devices: renumbered.length } : prev.main,
                                        };
                                    });
                                }}
                                addTooltip="Add device below"
                                removeTooltip="Remove device"
                                onRemove={handleRemoveDevice}
                                onUpdate={handleDeviceUpdate}
                            />

                            <SectionTable
                                title="Module"
                                sectionId="modules"
                                isOpen={openSectionId === "modules"}
                                onToggle={handleSectionToggle}
                                scrollContainerRef={scrollContainerRef}
                                scrollAnchorRef={scrollAnchorRef}
                                rows={setup.modules}
                                columns={[
                                    { key: "slot", label: "Slot", type: "number", increment: true, width: 36 },
                                    {
                                        key: "model",
                                        label: "Model",
                                        width: 90,
                                        tooltip: "Module.Model — saved to the site database and the local BTS",
                                    },
                                    {
                                        key: "moduleType",
                                        label: "Type",
                                        measureValues: MODULE_TYPES,
                                        width: 72,
                                        render: (row, onChange, rowIndex, arrow) => (
                                            <SetupSelect
                                                value={row.moduleType}
                                                options={MODULE_TYPES}
                                                onChange={onChange}
                                                {...arrow}
                                            />
                                        ),
                                    },
                                    {
                                        key: "type",
                                        label: "Module",
                                        width: 100,
                                        render: (row, onChange, rowIndex, arrow) => {
                                            const device = setup.devices.find((d) => d.uniqueId === row.deviceUniqueId)
                                                ?? setup.devices.find((d) => d.ipAddress === row.ipAddress);
                                            const options = getModuleModelOptions(row.moduleType, device?.type);
                                            const value = row.type || row.model || "";
                                            const opts = options.length
                                                ? (options.includes(value) ? options : [value, ...options].filter(Boolean))
                                                : (value ? [value] : []);
                                            return (
                                                <SetupSelect
                                                    value={value}
                                                    options={opts}
                                                    onChange={onChange}
                                                    {...arrow}
                                                />
                                            );
                                        },
                                    },
                                    {
                                        key: "ipAddress",
                                        label: "Dev",
                                        measureValues: deviceIpOptions,
                                        width: 90,
                                        render: (row, onChange, rowIndex, arrow) => (
                                            <SetupSelect
                                                value={row.ipAddress}
                                                options={deviceIpOptions.length ? deviceIpOptions : [row.ipAddress || ""]}
                                                onChange={onChange}
                                                {...arrow}
                                            />
                                        ),
                                    },
                                    { key: "startAt", label: "SA", type: "number", width: 40, tooltip: "Start At — leave blank to auto-calculate on save" },
                                    { key: "dataLength", label: "DL", type: "number", width: 40, tooltip: "Data Length — leave blank to auto-calculate on save" },
                                    {
                                        key: "hasAmbientTemperatureSensor",
                                        label: "TMP",
                                        width: 40,
                                        tooltip: "Has Ambient Temperature Sensor?",
                                        render: (row, onChange) => <SetupCheckbox checked={row.hasAmbientTemperatureSensor} onChange={onChange} />,
                                    },
                                    { key: "ambientTemperatureSensorPosition", label: "TT", type: "number", width: 32, tooltip: "Ambient Temperature Sensor Terminal" },
                                    { key: "ambientTemperatureSensorVoltageRange", label: "ATR", width: 52, tooltip: "Ambient Temperature Sensor Voltage Range" },
                                    {
                                        key: "hasHumiditySensor",
                                        label: "HUM",
                                        width: 40,
                                        tooltip: "Has Humidity Sensor?",
                                        render: (row, onChange) => <SetupCheckbox checked={row.hasHumiditySensor} onChange={onChange} />,
                                    },
                                    { key: "humiditySensorPosition", label: "HT", type: "number", width: 32, tooltip: "Humidity Terminal" },
                                    { key: "humiditySensorVoltageRange", label: "HVR", width: 52, tooltip: "Humidity Sensor Voltage Range" },
                                    {
                                        key: "isResistanceModule",
                                        label: "RES",
                                        width: 40,
                                        tooltip: "Is Resistance Input?",
                                        render: (row, onChange) => <SetupCheckbox checked={row.isResistanceModule} onChange={onChange} />,
                                    },
                                    { key: "resistanceSensorPosition", label: "RT", type: "number", width: 32, tooltip: "Resistance Terminal" },
                                    {
                                        key: "isRo1",
                                        label: "RO1",
                                        width: 40,
                                        tooltip: "Is First Resistance Output?",
                                        render: (row, onChange) => <SetupCheckbox checked={row.isRo1} onChange={onChange} />,
                                    },
                                    {
                                        key: "isRo2",
                                        label: "RO2",
                                        width: 40,
                                        tooltip: "Is Second Resistance Output?",
                                        render: (row, onChange) => <SetupCheckbox checked={row.isRo2} onChange={onChange} />,
                                    },
                                    { key: "batteryLife", label: "BL", type: "number", width: 40, tooltip: "BRX bypass terminal to activate when checking temperatures" },
                                ]}
                                onAddAfter={(i) => {
                                    setSetup((prev) => {
                                        const modules = [...prev.modules];
                                        const copy = modules[i];
                                        const device = prev.devices.find((d) => d.uniqueId === copy?.deviceUniqueId) ?? prev.devices[0];
                                        modules.splice(i + 1, 0, createDefaultModule((copy?.slot ?? i) + 1, copy, device));
                                        return { ...prev, modules };
                                    });
                                }}
                                addTooltip="Add module below"
                                removeTooltip="Remove module"
                                onRemove={(i) => removeRow("modules", i)}
                                onUpdate={handleModuleUpdate}
                            />

                            <SectionTable
                                title="Section"
                                sectionId="sections"
                                isOpen={openSectionId === "sections"}
                                onToggle={handleSectionToggle}
                                scrollContainerRef={scrollContainerRef}
                                scrollAnchorRef={scrollAnchorRef}
                                rows={setup.sections}
                                columns={[
                                    { key: "number", label: "Order", type: "number", increment: true, width: 40 },
                                    { key: "name", label: "Name", increment: true, width: 90 },
                                    {
                                        key: "ipAddress",
                                        label: "Dev",
                                        measureValues: deviceIpOptions,
                                        width: 90,
                                        tooltip: "Section Device",
                                        render: (row, onChange, rowIndex, arrow) => (
                                            <SetupSelect
                                                value={row.ipAddress}
                                                options={deviceIpOptions.length ? deviceIpOptions : [row.ipAddress || ""]}
                                                onChange={onChange}
                                                {...arrow}
                                            />
                                        ),
                                    },
                                    {
                                        key: "sectionType",
                                        label: "Type",
                                        measureValues: SECTION_TYPES,
                                        width: 90,
                                        render: (row, onChange, rowIndex, arrow) => {
                                            const value = row.sectionType ?? "";
                                            const options = value && !SECTION_TYPES.includes(value)
                                                ? [value, ...SECTION_TYPES]
                                                : SECTION_TYPES;
                                            return (
                                                <SetupSelect value={value} options={options} onChange={onChange} {...arrow} />
                                            );
                                        },
                                    },
                                    { key: "slotsUsed", label: "Slots", type: "number", width: 48, tooltip: "Slot Used" },
                                    { key: "cablesPerSlot", label: "CPS", type: "number", width: 48, tooltip: "Cables Per Slot" },
                                    {
                                        key: "cpsEdit",
                                        label: "",
                                        width: 52,
                                        tooltip: "Set Cables Per Slot",
                                        render: (row) => (
                                            <MuiMaterials.Button
                                                size="small"
                                                variant="outlined"
                                                title="Cables per slot (per slot)"
                                                onClick={() => setCpsDialogSectionId(row.id)}
                                                sx={{ minWidth: 48, py: 0.25, fontSize: 12 }}
                                            >
                                                CPS
                                            </MuiMaterials.Button>
                                        ),
                                    },
                                ]}
                                onAddAfter={(i) => {
                                    setSetup((prev) => {
                                        const sections = [...prev.sections];
                                        const copy = sections[i];
                                        const device = prev.devices.find((d) => d.uniqueId === copy?.deviceUniqueId) ?? prev.devices[0];
                                        const newSection = createDefaultSection(i + 2, copy, device);
                                        sections.splice(i + 1, 0, newSection);
                                        const renumbered = sections.map((s, idx) => ({ ...s, number: idx + 1 }));
                                        let sectionPositionRefs = syncSectionPositionRefsForSection(
                                            newSection,
                                            prev.sectionPositionRefs ?? []
                                        );
                                        sectionPositionRefs = sortSectionPositionRefs(
                                            renumbered,
                                            sectionPositionRefs
                                        );
                                        return {
                                            ...prev,
                                            sections: renumbered,
                                            sectionPositionRefs,
                                        };
                                    });
                                }}
                                addTooltip="Add section below"
                                removeTooltip="Remove section"
                                onRemove={handleRemoveSection}
                                onUpdate={handleSectionUpdate}
                            />

                            <SectionTable
                                title="Bin"
                                sectionId="bins"
                                isOpen={openSectionId === "bins"}
                                onToggle={handleSectionToggle}
                                scrollContainerRef={scrollContainerRef}
                                scrollAnchorRef={scrollAnchorRef}
                                rows={setup.bins}
                                columns={[
                                    { key: "number", label: "Order", type: "number", increment: true, width: 36 },
                                    {
                                        key: "type",
                                        label: "Type",
                                        measureValues: BIN_TYPES,
                                        width: 88,
                                        render: (row, onChange, rowIndex, arrow) => {
                                            const value = row.type ?? "";
                                            const options = value && !BIN_TYPES.includes(value)
                                                ? [value, ...BIN_TYPES]
                                                : BIN_TYPES;
                                            return (
                                                <SetupSelect value={value} options={options} onChange={onChange} {...arrow} />
                                            );
                                        },
                                    },
                                    { key: "name", label: "Name", increment: true, width: 80 },
                                    { key: "fans", label: "Fans", type: "number", width: 40 },
                                    { key: "cables", label: "Cables", type: "number", width: 44 },
                                    { key: "diameter", label: "Diam", type: "number", width: 40, tooltip: "Bin Diameter" },
                                    { key: "length", label: "Length", type: "number", width: 44, tooltip: "Flat house length (ft). BTS roof ridge runs along Length." },
                                    { key: "width", label: "Width", type: "number", width: 40, tooltip: "Flat house width (ft)" },
                                    { key: "height", label: "Wall", type: "number", width: 36, tooltip: "Wall Height" },
                                    { key: "peakHeight", label: "Peak", type: "number", width: 36, tooltip: "Peak Height" },
                                    { key: "peakLength", label: "PkLen", type: "number", width: 40, tooltip: "Flat house peak / ridge length (ft). Normally matches Length." },
                                    { key: "cableLayout", label: "Layout", width: 52, tooltip: "Cable Layout" },
                                ]}
                                onAddAfter={(i) => {
                                    setSetup((prev) => {
                                        const bins = [...prev.bins];
                                        bins.splice(i + 1, 0, createDefaultBin(bins.length + 1, bins[i]));
                                        const renumbered = renumberBins(bins);
                                        const binsWithLayout = applyBinCableLayouts(renumbered);
                                        const { cables, sensors } = syncCablesFromBins(
                                            binsWithLayout,
                                            prev.cables,
                                            prev.sections,
                                            prev.sensors
                                        );
                                        return {
                                            ...prev,
                                            bins: binsWithLayout,
                                            cables,
                                            sensors,
                                            fans: syncFansFromBins(renumbered, prev.fans, prev.devices),
                                            main: prev.main ? { ...prev.main, bins: renumbered.length } : prev.main,
                                        };
                                    });
                                }}
                                addTooltip="Add bin below"
                                removeTooltip="Remove bin"
                                onRemove={handleRemoveBin}
                                onUpdate={handleBinUpdate}
                            />

                            <SectionTable
                                title="Fan"
                                sectionId="fans"
                                isOpen={openSectionId === "fans"}
                                onToggle={handleSectionToggle}
                                scrollContainerRef={scrollContainerRef}
                                scrollAnchorRef={scrollAnchorRef}
                                rows={setup.fans}
                                columns={[
                                    { key: "fanName", label: "Name", increment: true, width: 90 },
                                    {
                                        key: "ipAddress",
                                        label: "Dev",
                                        measureValues: deviceIpOptions,
                                        width: 90,
                                        render: (row, onChange, rowIndex, arrow) => (
                                            <SetupSelect
                                                value={row.ipAddress}
                                                options={deviceIpOptions.length ? deviceIpOptions : [row.ipAddress || ""]}
                                                onChange={onChange}
                                                {...arrow}
                                            />
                                        ),
                                    },
                                    { key: "relayModule", label: "RM", type: "number", width: 40, tooltip: "Relay Module" },
                                    { key: "relayTerminal", label: "RT", type: "number", width: 32, tooltip: "Relay Terminal" },
                                    { key: "ampModule", label: "AM", type: "number", width: 40, tooltip: "Amp Module" },
                                    { key: "ampTerminal", label: "AT", type: "number", width: 32, tooltip: "Amp Terminal" },
                                    {
                                        key: "hasTransducer",
                                        label: "Transducer",
                                        width: 72,
                                        tooltip: "Has Transducer?",
                                        render: (row, onChange) => <SetupCheckbox checked={row.hasTransducer} onChange={onChange} />,
                                    },
                                    { key: "discreteModule", label: "DM", type: "number", width: 40, tooltip: "Discrete Module" },
                                    { key: "discreteTerminal", label: "DT", type: "number", width: 32, tooltip: "Discrete Terminal" },
                                ]}
                                onAddAfter={(i) => {
                                    setSetup((prev) => {
                                        const fans = [...prev.fans];
                                        const copy = fans[i];
                                        const bin = prev.bins.find((b) => b.binId === copy?.binId) ?? prev.bins[0];
                                        const device = prev.devices.find((d) => d.ipAddress === copy?.ipAddress) ?? prev.devices[0];
                                        fans.splice(i + 1, 0, createDefaultFan(fans.length + 1, copy, bin, device));
                                        const nextFans = fans.map((f, idx) => ({ ...f, fanNumber: idx + 1 }));
                                        return {
                                            ...prev,
                                            fans: nextFans,
                                            bins: updateBinFanCounts(prev.bins, nextFans),
                                        };
                                    });
                                }}
                                addTooltip="Add fan below"
                                removeTooltip="Remove fan"
                                onRemove={handleRemoveFan}
                                onUpdate={(i, k, v) => updateRow("fans", i, k, v)}
                            />

                            <SectionTable
                                title="Cable"
                                sectionId="cables"
                                isOpen={openSectionId === "cables"}
                                onToggle={handleSectionToggle}
                                scrollContainerRef={scrollContainerRef}
                                scrollAnchorRef={scrollAnchorRef}
                                rows={setup.cables}
                                paginateRows={false}
                                toolbar={(
                                    <>
                                        <MuiMaterials.Button
                                            size="small"
                                            variant="outlined"
                                            disabled={!setup.sections?.length}
                                            onClick={handleAssignSections}
                                        >
                                            Assign Sections
                                        </MuiMaterials.Button>
                                        <InfoHint
                                            title="Assigns sections to cables in bin order, filling each section slot by cables per slot (CPS)."
                                            ariaLabel="Assign sections help"
                                        />
                                    </>
                                )}
                                columns={[
                                    { key: "number", label: "Order", type: "number", increment: true, width: 48 },
                                    { key: "cableType", label: "Type", width: 96 },
                                    { key: "name", label: "Name", increment: true, width: 96 },
                                    { key: "sensorCount", label: "Sens", type: "number", width: 44 },
                                    {
                                        key: "sectionName",
                                        label: "Section",
                                        measureValues: sectionNameOptions,
                                        width: 100,
                                        render: (row, onChange, rowIndex, arrow) => (
                                            <SetupSelect
                                                value={row.sectionName ?? ""}
                                                options={["", ...sectionNameOptions]}
                                                onChange={(val) => {
                                                    onChange(val);
                                                    const section = val
                                                        ? setup.sections.find((s) => s.name === val)
                                                        : null;
                                                    updateRow("cables", rowIndex, "sectionId", section?.id ?? 0);
                                                }}
                                                {...arrow}
                                            />
                                        ),
                                    },
                                    { key: "sectionPosition", label: "Slot", type: "number", width: 44 },
                                    { key: "sectionPositionStartAt", label: "PTS", type: "number", width: 44, tooltip: "Pig Tail Start At" },
                                    { key: "sensorSpacing", label: "Spacing", type: "number", width: 60 },
                                    { key: "firstSensorHeight", label: "Height", type: "number", width: 56 },
                                ]}
                                onAddAfter={(i) => {
                                    setSetup((prev) => {
                                        const cables = [...prev.cables];
                                        const copy = cables[i];
                                        const bin = prev.bins.find((b) => b.binId === copy?.binId) ?? prev.bins[0];
                                        cables.splice(i + 1, 0, createDefaultCable((copy?.number ?? i) + 1, copy, bin));
                                        const nextCables = cables.map((c, idx) => ({ ...c, number: idx + 1 }));
                                        const bins = updateBinCableCounts(prev.bins, nextCables, prev.sensors);
                                        const { cables: synced, sensors } = syncCablesFromBins(
                                            bins,
                                            nextCables,
                                            prev.sections,
                                            prev.sensors
                                        );
                                        return { ...prev, bins, cables: synced, sensors };
                                    });
                                }}
                                addTooltip="Add cable below"
                                removeTooltip="Remove cable"
                                onRemove={handleRemoveCable}
                                onUpdate={handleCableUpdate}
                            />

                            <SectionTable
                                title="Sensor"
                                sectionId="sensors"
                                isOpen={openSectionId === "sensors"}
                                onToggle={handleSectionToggle}
                                scrollContainerRef={scrollContainerRef}
                                scrollAnchorRef={scrollAnchorRef}
                                rows={filteredSensorRows.rows}
                                toolbar={(
                                    <>
                                        <MuiMaterials.FormControlLabel
                                            control={(
                                                <MuiMaterials.Checkbox
                                                    size="small"
                                                    checked={sensorForcedOpenOnly}
                                                    onChange={(e) => setSensorForcedOpenOnly(e.target.checked)}
                                                    sx={{ p: 0.5 }}
                                                />
                                            )}
                                            label="Forced Open only"
                                            sx={{
                                                m: 0,
                                                mr: 1,
                                                alignItems: "center",
                                                "& .MuiFormControlLabel-label": {
                                                    lineHeight: 1,
                                                    fontSize: 14,
                                                },
                                            }}
                                        />
                                        <MuiMaterials.TextField
                                            size="small"
                                            value={sensorNameFilter}
                                            onChange={(e) => setSensorNameFilter(e.target.value)}
                                            placeholder="Filter by name or device IP"
                                            sx={{
                                                minWidth: 160,
                                                maxWidth: 240,
                                                "& .MuiInputBase-input": { py: 0.75, fontSize: 13 },
                                            }}
                                        />
                                    </>
                                )}
                                columns={[
                                    { key: "number", label: "Order", type: "number", increment: true, width: 40 },
                                    { key: "name", label: "Name", increment: true, width: 90 },
                                    { key: "type", label: "Type", width: 90 },
                                    { key: "deviceIp", label: "BIP", width: 90, tooltip: "Banner Device IP Address" },
                                    { key: "deviceId", label: "BID", type: "number", width: 40, tooltip: "Banner Device ID" },
                                    {
                                        key: "cableName",
                                        label: "Cable",
                                        measureValues: cableNameOptions,
                                        width: 90,
                                        render: (row, onChange, rowIndex, arrow) => (
                                            <SetupSelect
                                                value={row.cableName}
                                                options={cableNameOptions.length ? cableNameOptions : [row.cableName || ""]}
                                                onChange={(val) => {
                                                    onChange(val);
                                                    const cable = setup.cables.find((c) => c.name === val);
                                                    if (cable) {
                                                        const orig = mapSensorRowIndex(rowIndex);
                                                        updateRow("sensors", orig, "cableId", cable.id);
                                                        updateRow("sensors", orig, "binId", cable.binId);
                                                        updateRow("sensors", orig, "binName", cable.binName);
                                                    }
                                                }}
                                                {...arrow}
                                            />
                                        ),
                                    },
                                    { key: "position", label: "Pos", type: "number", width: 40 },
                                    {
                                        key: "forcedOpen",
                                        label: "FO",
                                        width: 40,
                                        tooltip: "Forced Open — mark this sensor as forced open.",
                                        render: (row, onChange) => (
                                            <MuiMaterials.Checkbox
                                                size="small"
                                                checked={!!row.forcedOpen}
                                                onChange={(e) => onChange(e.target.checked)}
                                                sx={{ p: 0 }}
                                            />
                                        ),
                                    },
                                ]}
                                onAddAfter={(i) => {
                                    const orig = mapSensorRowIndex(i);
                                    setSetup((prev) => {
                                        const sensors = [...prev.sensors];
                                        const copy = sensors[orig];
                                        const cable = prev.cables.find((c) => c.id === copy?.cableId) ?? prev.cables[0];
                                        const bin = prev.bins.find((b) => b.binId === copy?.binId) ?? prev.bins[0];
                                        sensors.splice(orig + 1, 0, createDefaultSensor((copy?.number ?? orig) + 1, copy, cable, bin));
                                        return {
                                            ...prev,
                                            sensors: sensors.map((s, idx) => ({ ...s, number: idx + 1, position: idx + 1 })),
                                        };
                                    });
                                }}
                                addTooltip="Add sensor below"
                                removeTooltip="Remove sensor"
                                onRemove={(i) => removeRow("sensors", mapSensorRowIndex(i))}
                                onUpdate={(i, k, v) => updateRow("sensors", mapSensorRowIndex(i), k, v)}
                            />

                            <SetupTasksPanel
                                isOpen={openSectionId === "tasks"}
                                onToggle={() => handleSectionToggle("tasks")}
                                isAutoScanOn={isAutoScanOn}
                                taskSchedule={setup.taskSchedule}
                                taskSelectedBins={setup.taskSelectedBins}
                                binNames={taskBinNames}
                                onAutoScanChange={handleAutoScanChange}
                                onBinsChange={handleTaskBinsChange}
                                onSlotChange={handleTaskSlotChange}
                                border={border}
                                textSecondary={textSecondary}
                                paper={paper}
                            />

                            <SetupConfigPanel
                                isOpen={openSectionId === "setupConfig"}
                                onToggle={() => handleSectionToggle("setupConfig")}
                                siteGuid={loadedSiteGuid}
                                border={border}
                                textSecondary={textSecondary}
                                paper={paper}
                            />
                        </MuiMaterials.Box>
                        </MuiMaterials.Box>
                </MuiMaterials.Box>
                ) : null}
            </div>
            <ConfirmDialog
                open={saveSetupConfirmOpen}
                onClose={() => setSaveSetupConfirmOpen(false)}
                title="Save site setup?"
                titleId="save-site-setup-title"
                message="This writes devices, bins, cables, and the other setup sections. It does not save Config."
                actions={(
                    <>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            sx={confirmDialogButtonSx()}
                            onClick={() => setSaveSetupConfirmOpen(false)}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            sx={confirmDialogButtonSx()}
                            onClick={() => {
                                setSaveSetupConfirmOpen(false);
                                handleSave();
                            }}
                        >
                            Save Setup
                        </MuiMaterials.Button>
                    </>
                )}
            />
            <SectionCpsDialog
                open={cpsDialogSectionId != null}
                section={cpsDialogSection}
                sectionPositionRefs={setup.sectionPositionRefs}
                onClose={() => setCpsDialogSectionId(null)}
                onSave={handleSaveSectionCps}
            />
            <NewSiteDialog
                open={newSiteDialogOpen}
                customerName={newCustomerName}
                siteName={newSiteName}
                busy={creatingSite}
                customerOptions={createSiteCustomerOptions}
                siteNameOptions={createSiteNameOptions}
                optionsLoading={createSiteOptionsLoading}
                onCustomerNameChange={setNewCustomerName}
                onSiteNameChange={setNewSiteName}
                onClose={() => {
                    if (creatingSite) return;
                    setNewSiteDialogOpen(false);
                }}
                onCreate={handleCreateNewSite}
            />
        </MuiMaterials.ThemeProvider>
    );
};

export default SiteSetupWindow;
